import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  afterNextRender,
  TemplateRef,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
  viewChild,
  HostListener,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { catchError, map, of } from "rxjs";
import { MatIconModule } from "@angular/material/icon";
import { OmniarkDrawerService } from "@app/shared/services/drawer.service";
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from "@angular/cdk/drag-drop";
import {
  EnrTooltipDirective,
  OmniarkUiModule,
  SelectOption,
} from "@corp-ais/omniark-ui";
import {
  catalogFigureText,
  ClearableNumber,
  CUSTOMER_PERSONAS,
  DetailAgreementRef,
  EnrichmentArpu,
  EnrichmentDetail,
  EnrichmentDisplayItem,
  EnrichmentListPriceItem,
  EnrichmentPriceItem,
  extractPriceAmount,
  extractPriceUnit,
  LocaleMap,
  LocalizedAttachment,
  LocalizedText,
  PRICE_DISCOUNT_DURATION_UNITS,
  PriceDiscount,
  PriceExternalIdentifier,
  PriceTax,
  PriceUnitOfMeasure,
  ProductOfferingTerm,
  resolveLocalized,
  ShelfRef,
  toClearableNumber,
} from "@app/shared/models/enrichment-detail.model";
import { MastersService } from "@app/core/services/masters.service";
import { PermissionService } from "@core/services/permission.service";
import { mayAuthorCatalogOwnedFigures } from "../../../utils/price-authority.util";
import {
  ActiveLanguage,
  LanguageConfigStore,
} from "@app/shared/services/language-config.store";
import { LanguageModeService } from "@app/shared/services/language-mode.service";
import { EnrichmentService } from "@app/shared/services/enrichment.service";
import {
  ENRICHMENT_HEADER_TRANSPORT,
  EnrichmentDataService,
} from "../../../services/enrichment-data.service";
import {
  PRICE_CONFIG_TAB,
  normalizeTabId,
} from "../enrichment-edit-package/tab-algebra.util";
import { FieldFocus } from "../field-focus";
import {
  FlatEnrichmentModel,
  FlatPOP,
  isFlatEnrichmentModel,
} from "@app/shared/mappers/enrichment-detail.adapter";
import {
  isAiChatbotChannel,
  isNetworkTypeChannel,
  isVoiceIvrChannel,
  isImChannel,
  voiceChannelLabel,
  isExactMyAisChannel,
  isMyAisChannel,
  isOnlineStoreChannel,
} from "@app/shared/constants/channel.constants";
import { PricingLocaleInput } from "./pricing-locale-input/pricing-locale-input";
import { TermRichEditor } from "@shared/components/term-rich-editor/term-rich-editor";
import { newlinesToPipes, pipesToNewlines } from "./keyselling-linebreaks";
import { EnrichmentAttachmentUploader } from "../enrichment-attachment-uploader/enrichment-attachment-uploader";
import { EnrichmentPriceShelf } from "./enrichment-price-shelf/enrichment-price-shelf";
import {
  EnrichmentPriceClassification,
  PriceClassificationChange,
  PriceClassificationValues,
} from "./enrichment-price-classification/enrichment-price-classification";
import {
  AgreementUsage,
  effectiveAgreement,
  EnrichmentPriceAgreement,
} from "./enrichment-price-agreement/enrichment-price-agreement";
import { OverlayModule, ConnectedPosition } from "@angular/cdk/overlay";
import { EnrichmentIconPicker } from "../enrichment-icon-picker/enrichment-icon-picker";
import {
  badgeIconKeepsColor,
  IconChoice,
  isColorfulIcon,
} from "../../../utils/icon-catalog.util";
import { EnrichmentIconCatalog } from "../../../services/enrichment-icon-catalog.service";
import {
  ZONED_ISO_PATTERN,
  formatCivrDate,
  parseCivrDate,
} from "../../../utils/selling-window.util";
import { validatePriceDiscount } from "../../../services/publish/validators/flat-detail.validator";
import { EnrichmentPriceDirtyStore } from "../enrichment-edit-package/enrichment-price-dirty.store";
import { EnrichmentPriceLockStore } from "../enrichment-edit-package/enrichment-price-lock.store";
import { EnrichmentIsSofEditStore } from "../enrichment-edit-package/enrichment-is-sof-edit.store";
import { isSofRule, resolveIsSof } from "@app/shared/utils/is-sof-rule.util";
import { EnrichmentDisplayQuantityLockStore } from "../enrichment-edit-package/enrichment-display-quantity-lock.store";
import {
  DecoratedDisplay,
  QuantityProvenance,
} from "../../../models/service-quantity.model";
import { SectionDirtyStore } from "../enrichment-edit-package/section-dirty.store";
import { ActivePriceStore } from "../enrichment-edit-package/active-price.store";
import { ActivePriceReadiness } from "../enrichment-edit-package/active-price-readiness";
import { buildProductDetailAppLink } from "@app/shared/utils/adl-deep-link.util";
import { formatDate } from "@app/shared/utils/date";
import {
  VALID_FOR_BADGE_COLOR,
  ValidForView,
  describeValidFor,
} from "./valid-for.util";
import { productCodeOf } from "../../../utils/price-entry.util";
import { firstIconUrl } from "../../../mappers/display-attachment.util";
import { ServiceKindBadge, serviceKindBadge } from "./service-kind-badge";
import {
  SECTIONS,
  SectionGroupId,
  SectionGroupView,
  SectionNav,
  groupOfSection,
  groupSections,
} from "./pricing-sections";
import { formatFigures, priceFigures } from "../../../utils/price-amount.util";
import { sectionForTarget } from "./section-target.util";
import { EnrichmentPricingGroupStore } from "./enrichment-pricing-group.store";
import {
  BADGE_COLOR_LABELS,
  BADGE_COLOR_ORDER,
  BADGE_COLOR_ORDER_AICHATBOT,
  BADGE_COLOR_ORDER_DETAIL,
  BADGE_COLOR_TEXT_DEFAULTS,
  BADGE_COLOR_TYPES,
  BADGE_ICON_POSITIONS,
  BADGE_TEMPLATES,
  BADGE_TEMPLATES_DETAIL,
  BadgeColorType,
  BadgeIconPosition,
  DEFAULT_BADGE_ICON_POSITION,
  DEFAULT_BADGE_TEXT_COLOR,
  EnrichmentBadgeTemplate,
  EnrichmentLanguage,
  UPSELL_BADGE_COLOR_BY_LIST_COLOR,
  UPSELL_BADGE_COLOR_ORDER,
  badgeColorCss,
} from "../../../constants/enrichment.constants";
import {
  BADGE_DETAIL_DISPLAY_NAME,
  BADGE_DISPLAY_NAME,
  BadgeFormValue,
  buildBadgeValue,
  emptyBadge,
  isBadgeEmpty,
  parseBadgeValue,
} from "../../../mappers/badge.codec";
import {
  NON_SERVICE_DISPLAY_NAMES,
  UPSELL_POPUP_DISPLAY_NAME,
  UpsellPopupFormValue,
  parseUpsellPopupValue,
  recordOf,
  stringMapOf,
} from "../../../mappers/upsell-popup.codec";
import { PreviewSpotlightService } from "../../../services/preview-spotlight.service";
import { PricingPackageDrawer } from "./pricing-package-drawer/pricing-package-drawer";

interface TaxRow {
  category: string;
  rate: string;
  amount: string;
  unit: string;
}

/** One selectable customer-persona row. Mirrors the `/master/persona` wire
 *  shape; `personaGroup` is optional only because a stored persona merged
 *  back into the list may predate the group key. */
interface PersonaRow {
  personaGroup?: string;
  personaType: string;
}

/** Offline persona catalogue — the options shown until `/master/persona`
 *  answers, and kept when the lookup errors or returns nothing. */
const PERSONA_FALLBACK: readonly PersonaRow[] = CUSTOMER_PERSONAS;

/** A view-only reference-identifier chip in the pane head. */
interface ExternalIdRow {
  /** Stable key for trackBy — the raw `externalIdentifierType`. */
  key: string;
  /** Marketing-facing label (e.g. "Price code"). */
  label: string;
  /** The identifier value (e.g. "OP_e7f1cded"). */
  value: string;
  /** Owning system (e.g. "CPC") — shown as a tooltip. */
  owner: string;
}

/** Per-price clearable strings (Go `*string` on the BE): absent inherits,
 *  "" clears. */
type PriceTextKey =
  | "offerPackageStatus"
  | "patternPlayVoice"
  | "patternEnsurePK"
  | "patternPlayMatch"
  | "tariffContract"
  | "UrlSubdirectory-1"
  | "UrlSubdirectory-2";

/** A blank emission still means "write nothing" — see
 *  {@link EnrichmentPricing.setCivrFlag}. No longer offered as an OPTION (the
 *  select is Yes/No now), but `civrAsText` turns a null/undefined emission
 *  into "", and that must never be mistaken for an authored "No". */
const CIVR_FLAG_UNSET = "";

const APP_HEADER_PX = 64;
const SECTION_AIR_PX = 16;
const FORM_VIEWPORT_TOP_PX = APP_HEADER_PX + SECTION_AIR_PX;
const SPY_BOUNDARY_SLACK_PX = 12;
const SPY_ACTIVE_BAND_BOTTOM = "-45%";

/** Two-way flag options.
 *
 *  "Not set" used to sit here as a third choice, and it was wrong twice over:
 *    - to the AUTHOR it was indistinguishable from No — an unanswered flag and
 *      an answered "no" behave identically downstream, so the extra state
 *      bought a decision nobody could act on, and
 *    - worse, picking it on a flag already stored as "Y" did NOTHING. It
 *      deletes the key, the key is then absent from the PUT, and the BE reads
 *      absent as "inherit" (`applyCIRVFlag` returns the characteristics
 *      untouched when the pointer is nil) — so the Y survived. The one thing
 *      an author would use "Not set" for was the one thing it could not do.
 *
 *  Yes/No only. An unanswered flag DISPLAYS as No (see `civrFlag`) because
 *  that is how it already behaves, and picking No now genuinely writes "N",
 *  which is what turns a flag off. */
const CIVR_FLAG_OPTIONS: SelectOption[] = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

/** PLAY_PRICE vocabulary. The BE validates this against the same closed set
 *  and rejects anything else, so the select must not be free-text. */
/** BA-fixed kind list for user-created displayOther services (wire values). */
export const DISPLAY_KIND_VALUES: readonly string[] = [
  "internet",
  "verticalapp",
  "voice",
  "wifi",
  "content",
  "sms",
  "mms",
  "internetRoaming",
  "smsRoaming",
  "voiceRoaming",
  "vas",
  "device",
  "broadband",
  "enterprise",
  "fee",
  "combo",
  "broadbandspeed",
  "other",
];

export const PLAY_PRICE_OPTIONS: SelectOption[] = [
  { label: "Price excluding VAT", value: "PRICE_EXCLUDE_VAT" },
  { label: "Price including VAT", value: "PRICE_INCLUDE_VAT" },
];

/* `parseCivrDate` / `formatCivrDate` / `ZONED_ISO_PATTERN` now live in
 * `utils/selling-window.util` — imported above rather than defined here.
 * The SAVE path seeds an unset Selling window bound from the ValidFor, and
 * the value it writes has to be byte-identical to the one these pickers
 * display; one shared implementation is what guarantees that. */

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** "HH:mm" of a Date, in LOCAL terms — the clock the author sees. */
function clockOf(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** `date`'s calendar day carrying `clock` ("HH:mm"); a junk clock reads 00:00. */
function withClock(date: Date, clock: string): Date {
  const [h, m] = clock.split(":").map(Number);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    Number.isFinite(h) ? h : 0,
    Number.isFinite(m) ? m : 0,
    0,
  );
}

/** Midnight of `date`'s calendar day. */
function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Serialise the Upsell pop-up's `validFor` bounds as RFC3339.
 *
 * This must NOT use `formatCivrDate`: Selling Window fields deliberately use
 * a zone-less local overlay format, whereas `displayPopupUpSell.validFor` is
 * validated by the backend as an RFC3339 instant. Keeping the local calendar
 * components avoids the UTC day-shift for an ICT author while the explicit
 * offset makes the instant unambiguous. Milliseconds are included to match
 * the enrichment API's existing `validFor` contract.
 */
function formatUpsellPopupDateTime(date: Date): string {
  const pad3 = (n: number): string => String(n).padStart(3, "0");
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteOffset = Math.abs(offsetMinutes);
  const offset = `${sign}${pad2(Math.floor(absoluteOffset / 60))}:${pad2(
    absoluteOffset % 60,
  )}`;

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate(),
  )}T${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(
    date.getSeconds(),
  )}.${pad3(date.getMilliseconds())}${offset}`;
}

/**
 * Clock choices for the selling-window Time selects: a 30-minute grid plus
 * 23:59.
 *
 * 23:59 is listed because it is what an expiry almost always wants ("sellable
 * through the last day") and no round grid contains it. The grid is 30 rather
 * than 15 minutes so the list stays scannable; anything off-grid is still
 * reachable, because a stored off-grid time is appended to the options (see
 * `clockOptions`) and the select is searchable.
 */
const SELLING_WINDOW_CLOCKS: readonly string[] = [
  ...Array.from(
    { length: 48 },
    (_, i) => `${pad2(Math.floor(i / 2))}:${i % 2 ? "30" : "00"}`,
  ),
  "23:59",
];

/**
 * Reference-stability guard for every `Date` bound into an `enr-input` picker.
 *
 * The picker reaches Angular's `NgModel` through `[value]`, and `NgModel`
 * diffs with `Object.is`. A freshly-parsed `Date` handed back on every
 * change-detection pass therefore reads as a NEW value each pass: `NgModel`
 * queues a microtask write, the microtask keeps the zone unstable, the zone
 * ticks again, the binding re-parses — an unbounded tick loop that pegs the
 * tab and dies on a heap OOM. Only ever surfaced once a date was authored,
 * because the unauthored fallback (`sellingWindowMin()`) was already a
 * memoised computed.
 *
 * Used as the `equal` of the date-valued computeds below so the SAME instant
 * keeps the SAME instance, even though every keystroke on this tab re-clones
 * the selected price.
 */
const sameInstant = (a: Date | string, b: Date | string): boolean =>
  a instanceof Date && b instanceof Date
    ? a.getTime() === b.getTime()
    : a === b;

function optionalFiniteNumber(raw: string): number | undefined {
  const value = raw.trim();
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Pricing tab — edits the one price the URL names (`ActivePriceStore`).
 *
 * Edits flow back through `EnrichmentDataService.setEnrichment`. The
 * write-back path mutates a deep clone of the model:
 *   - the flat `__detail.prices[i]` is the source of truth (the adapter's
 *     `modelToDetail` reads passthrough fields like `tax` / `unitOfMeasure`
 *     / `productOfferingTerm` / `isTaxIncluded` directly from there)
 *   - legacy POP fields (`name`, `description`, `price.unit`, `__displays`)
 *     are mirrored so the adapter's wire-format flatten path keeps working
 *     for the wire body
 */
@Component({
  selector: "app-enrichment-pricing",
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    DragDropModule,
    OmniarkUiModule,
    EnrTooltipDirective,
    OverlayModule,
    PricingLocaleInput,
    EnrichmentAttachmentUploader,
    EnrichmentIconPicker,
    EnrichmentPriceShelf,
    EnrichmentPriceClassification,
    EnrichmentPriceAgreement,
    PricingPackageDrawer,
    TermRichEditor,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./enrichment-pricing.html",
  styleUrls: ["./enrichment-pricing.scss"],
})
export class EnrichmentPricing implements OnDestroy {
  private readonly data = inject(EnrichmentDataService);
  private readonly active = inject(ActivePriceStore);
  private readonly enrichmentService = inject(EnrichmentService);
  /** Per-price dirty set for the save fan-out. Optional: provided by the
   *  edit-package shell; absent when the tab is mounted standalone in specs. */
  private readonly priceDirty = inject(EnrichmentPriceDirtyStore, {
    optional: true,
  });
  /** Which Service quantities the author has unlocked, and what each showed
   *  when opened. In the shell for the same reason as `priceLock` — and the
   *  snapshot rides along with the unlock so a tab switch cannot strand it.
   *  Optional likewise; absent means every quantity reads as locked. */
  private readonly quantityLock = inject(EnrichmentDisplayQuantityLockStore, {
    optional: true,
  });
  /** Which prices the author has unlocked for amount edits. Lives in the
   *  shell, not here, because this component is destroyed on every tab
   *  switch while the edits it made do not — see the store's own docs.
   *  Optional for the same reason as `priceDirty` above; absent means every
   *  price reads as locked, which is the safe default. */
  /** Which prices had their Source of Fund changed — in the shell for the same
   *  reason as `priceLock`. Optional so the component mounts standalone. */
  private readonly isSofEdits = inject(EnrichmentIsSofEditStore, {
    optional: true,
  });
  private readonly priceLock = inject(EnrichmentPriceLockStore, {
    optional: true,
  });
  /** Section-dirty attribution — which TAB the discard snapshot should
   *  blame. Optional for the same reason as `priceDirty` above. */
  private readonly sectionDirty = inject(SectionDirtyStore, {
    optional: true,
  });
  private readonly groupStore =
    inject(EnrichmentPricingGroupStore, { optional: true }) ??
    new EnrichmentPricingGroupStore();
  private readonly readiness = inject(ActivePriceReadiness, { optional: true });
  private readonly languageConfig = inject(LanguageConfigStore);
  private readonly languageMode = inject(LanguageModeService);
  protected readonly previewSpotlight = inject(PreviewSpotlightService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly injector = inject(Injector);
  private readonly iconCatalog = inject(EnrichmentIconCatalog);
  private readonly masters = inject(MastersService);
  private readonly permissionService = inject(PermissionService);

  // Warm the CDN icon catalog so the preset chips can show real icons. The
  // fetch is shared + cached, so this composes with the icon picker's load().
  private readonly iconCatalogLoad = this.iconCatalog
    .load()
    .pipe(takeUntilDestroyed())
    .subscribe();

  /** name → url map from the loaded icon catalog, for resolving preset icons. */
  private readonly iconUrlByName = computed(
    () => new Map(this.iconCatalog.icons().map((i) => [i.name, i.url])),
  );

  /** Resolve a localized value (name, currency unit, …) in the editor's active
   *  view language. Reads `activeLocale()` so the dependent display (price chip,
   *  "Selected price" heading, currency labels) re-renders when the language
   *  switch changes. */
  private resolveActive(value: LocalizedText | undefined): string {
    // A picked language → strict (that locale only); "ALL" → display priority
    // EN → TH → first other active locale, skipping empties.
    return this.languageMode.resolveForView(value);
  }

  private readonly model = toSignal(this.data.enrichment$, {
    initialValue: null,
  });

  protected readonly detail = computed<EnrichmentDetail | null>(() => {
    const m = this.model();
    return m && isFlatEnrichmentModel(m) ? m.__detail : null;
  });

  protected readonly prices = computed<EnrichmentPriceItem[]>(
    () => this.detail()?.prices ?? [],
  );

  /** Active editing locales — feed into every `pricing-locale-input`. */
  protected readonly languages = computed<ActiveLanguage[]>(() =>
    this.languageConfig.active(),
  );

  protected readonly selectedPriceId = this.active.popId;

  readonly focus = input<FieldFocus | null>(null);

  /** Package selected for the Upsell pop-up. Its id is persisted to
   * `displayPopupUpSell.relatedPrice`; the full item stays here for the card. */
  protected readonly selectedPackage = signal<EnrichmentListPriceItem | null>(
    null,
  );

  /** Service-display whose card is force-expanded because a readiness issue (or
   *  a preview pencil) navigated to it. Bound one-way to each card's
   *  `[expanded]`; manual toggles still work because Angular only writes the
   *  input when this value actually changes. */
  protected readonly expandedDisplay = signal<string | null>(null);

  /** Reference instant for Window state (Upcoming / Ending soon / Expired).
   *  Taken once per mount: the states move in days, not minutes. A signal so
   *  specs can pin it. */
  protected readonly now = signal(new Date());

  protected readonly selectedValidFor = computed<ValidForView | undefined>(() =>
    describeValidFor(this.selectedPrice()?.validFor, this.now()),
  );
  protected readonly validForBadgeColor = VALID_FOR_BADGE_COLOR;

  /** Net Gift completeness alert for the SELECTED price — when
   *  `isOfferingGift` is true the provisioning trio must all exist:
   *  `offeringGift.costCodePrice`, `offeringGift.vatAmount`, and `ussdNo`.
   *  Returns the human-readable names of whatever is missing; empty = no
   *  alert. Deliberately NOT part of publish readiness (BA call) — it is an
   *  in-form notice rendered above the Product name section, because all
   *  three values are catalog-owned and can only be fixed at the source. */
  protected readonly giftAlertMissing = computed<string[]>(() => {
    const p = this.selectedPrice();
    if (!p || p.isOfferingGift !== true) return [];
    const missing: string[] = [];
    if (!catalogFigureText(p.offeringGift?.costCodePrice)) {
      missing.push("cost code price");
    }
    if (!catalogFigureText(p.offeringGift?.vatAmount)) {
      missing.push("VAT amount");
    }
    if (!catalogFigureText(p.ussdNo)) missing.push("USSD No.");
    return missing;
  });

  /** The myAIS deep link for the selected price — `""` hides the control
   *  rather than offering a link that fails on a phone.
   *
   *  Empty in three cases: the editing scope is not myAIS (the link opens the
   *  myAIS app, so it means nothing elsewhere — `isExactMyAisChannel`, so an
   *  unpicked scope does not count), or the catalog has no Product code for
   *  the selected price. The code — not `detail()?.id`, which is the offering
   *  id — is the only thing the link carries. */
  protected readonly appLink = computed<string>(() => {
    if (!isExactMyAisChannel(this.channel())) return "";
    return buildProductDetailAppLink(this.selectedProductCode());
  });

  /** Inline post-click state for the deep-link button (the label flips to
   *  "Generated") — a signal rather than the
   *  toast service on purpose: `OmniarkToastService` is `providedIn: "root"`, so
   *  injecting it here (even optionally) resolves it and then blows up on ITS
   *  dependency in every spec that mounts this component bare. The Overview tab
   *  solves its own copy feedback the same way. */
  protected readonly appLinkCopied = signal(false);
  private appLinkCopiedTimer: ReturnType<typeof setTimeout> | null = null;

  /** Copy the deep link. Clipboard API with the textarea fallback the list page
   *  also relies on — not every browser this page runs in is a secure context. */
  protected copyAppLink(): void {
    const link = this.appLink();
    if (!link) return;
    this.writeToClipboard(link, () => this.flashAppLinkCopied());
  }

  /** Clipboard write: the async API when the page is a secure context, the
   *  textarea trick the list page also relies on when it isn't. */
  private writeToClipboard(text: string, done: () => void): void {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => undefined);
      return;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
    done();
  }

  private flashAppLinkCopied(): void {
    this.appLinkCopied.set(true);
    if (this.appLinkCopiedTimer) clearTimeout(this.appLinkCopiedTimer);
    this.appLinkCopiedTimer = setTimeout(
      () => this.appLinkCopied.set(false),
      2000,
    );
  }

  protected readonly selectedPrice = this.active.price;

  /** Reference-stable bound shelves for the per-price Shelf picker. Every
   *  keystroke re-clones the selected price (so `p.shelves` is a fresh array
   *  even when unchanged); a shelf-id-based `equal` keeps the SAME reference
   *  across edits that don't touch shelves, so the picker's auto-expand effect
   *  and per-node checks don't re-run on every character. */
  protected readonly selectedPriceShelves = computed<ShelfRef[]>(
    () => this.selectedPrice()?.shelves ?? [],
    {
      equal: (a, b) =>
        a.length === b.length && a.every((s, i) => s.id === b[i]?.id),
    },
  );

  /* ── Display helpers (per-section) ─────────────────────────────────── */

  protected readonly selectedPriceName = computed<string>(() => {
    const p = this.selectedPrice();
    const name =
      p &&
      (this.resolveActive(p.systemName).trim() || this.resolveActive(p.name));
    return name || "Untitled price";
  });

  /** Catalog reference identifiers for the selected price (POP code / id,
   *  offering id, variant id). View-only chips in the pane head — the
   *  ProductOfferingPriceCode is the same code price-code search matches on.
   *  Ordered by `EXTERNAL_ID_LABELS` so the most useful codes lead; entries
   *  without a value are dropped. */
  protected readonly externalIdentifiers = computed<ExternalIdRow[]>(() => {
    const list = this.selectedPrice()?.externalIdentifier ?? [];
    const promoted = this.selectedProductCode();
    const priceId = this.selectedPrice()?.id ?? "";
    return (
      list
        .filter((e): e is PriceExternalIdentifier => !!e?.id?.trim())
        .map((e) => ({
          key: e.externalIdentifierType ?? "",
          label: externalIdLabel(e.externalIdentifierType),
          value: e.id as string,
          owner: e.owner ?? "",
        }))
        // Whatever the header already shows is dropped here: the same code
        // twice, in two different treatments, reads as two codes.
        .filter((e) => e.value !== promoted && e.value !== priceId)
        .sort((a, b) => externalIdRank(a.key) - externalIdRank(b.key))
    );
  });

  protected readonly selectedProductCode = computed<string>(() => {
    const p = this.selectedPrice();
    return p ? productCodeOf(p) : "";
  });

  trackExternalId = (_: number, e: ExternalIdRow): string =>
    `${e.key}::${e.value}`;

  /** True when the selected POP is a Net Gift offering — reveals the cost-code
   *  price card + "Net Gift" badge below the Price. */
  protected readonly selectedIsOfferingGift = computed<boolean>(
    () => this.selectedPrice()?.isOfferingGift === true,
  );

  /** USSD access number of the selected price (BE `prices[].ussdNo`), shown
   *  read-only on the Product-name section. Empty string → the row is hidden. */
  protected readonly selectedUssdNo = computed<string>(() =>
    catalogFigureText(this.selectedPrice()?.ussdNo),
  );

  /** Cost-code price of a Net Gift offering (BE `offeringGift.costCodePrice`,
   *  a numeric string), as a display string. */
  protected readonly costCodePriceAmount = computed<string>(() => {
    return (
      catalogFigureText(this.selectedPrice()?.offeringGift?.costCodePrice) ||
      "—"
    );
  });

  /** VAT on the Net Gift cost code (BE `offeringGift.vatAmount`), as authored.
   *  Empty string when BE ships no VAT — the template then hides the VAT and
   *  total rows rather than showing a misleading 0.00. */
  protected readonly costCodeVatAmount = computed<string>(() => {
    return catalogFigureText(this.selectedPrice()?.offeringGift?.vatAmount);
  });

  /** Gross gift price — `costCodePrice + vatAmount`. The BE pair is
   *  authoritative (never re-derived from `tax[].taxRate`, and `isTaxIncluded`
   *  does not apply here: it describes `price.value`). Empty string when
   *  either half is missing or non-numeric. */
  protected readonly costCodePriceInclVat = computed<string>(() => {
    const gift = this.selectedPrice()?.offeringGift;
    const netText = catalogFigureText(gift?.costCodePrice);
    const vatText = catalogFigureText(gift?.vatAmount);
    if (!netText || !vatText) return "";
    const net = Number(netText);
    const vat = Number(vatText);
    if (!Number.isFinite(net) || !Number.isFinite(vat)) return "";
    // Round away binary-float noise (205.6 + 14.4 → 220.00000000000003).
    return (Math.round((net + vat) * 100) / 100).toFixed(2);
  });

  /* --- Authored price amounts (dutyFreeAmount / taxIncludedAmount) --------
   *
   * The two money characteristics the Price card edits. Authoring either one
   * makes the BE recompute the channel child POP —
   *   price.value            = taxIncludedAmount
   *   tax[0].taxAmount.value = taxIncludedAmount - dutyFreeAmount
   * — so `applyDerivedAmounts` mirrors that arithmetic locally and the Tax
   * card + mobile preview stay truthful before the save lands.
   *
   * Both are inherit-on-absent: an emptied field does NOT clear the stored
   * amount (BE reads absent as "keep what you have"), which is why the card
   * shows a "leave blank to keep N" hint instead of a clear affordance. */

  /** The author may change the price itself: they hold `enrichment:publish`.
   *  That is the whole bar, and it is the backend's own — the PATCH drops
   *  both amounts without that permission and keeps them with it.
   *
   *  It used to demand the Administrator role on top. Nothing on the server
   *  asked for that, so a role holding `enrichment:publish` was shown a
   *  read-only price it was entitled to change, and could change anyway
   *  through the API. The gate now matches what the save will accept.
   *
   *  This DISABLES the inputs rather than hiding them (the section still
   *  shows what the package costs, with a Read-only pill saying why), which
   *  is the same treatment every other read-only surface in this menu gets.
   *
   *  Read as a SIGNAL — `permissions()` not `hasPermission` — so the computed
   *  re-evaluates when the user switches role in the navbar, matching
   *  `mayPublish` / `mayUpdate` on the edit shell. */
  protected readonly mayEditPrice = computed<boolean>(() =>
    mayAuthorCatalogOwnedFigures(this.permissionService),
  );

  /** The author may change a **Service quantity** — the number on a service
   *  display (20 GB), not money.
   *
   *  Same gate as the money pair, and deliberately NOT the same computed:
   *  these are different authorities over different things, and folding them
   *  together would make `mayEditPrice` lie about what it guards. They
   *  coincide today because the BE authorizes a display's amount on
   *  `enrichment:publish`, the very permission behind the money pair
   *  (BFF ADR 0002).
   *
   *  Read as a SIGNAL so the computed re-evaluates on a role switch. */
  protected readonly mayEditServiceQuantity = computed<boolean>(() =>
    mayAuthorCatalogOwnedFigures(this.permissionService),
  );

  /** Has the author unlocked THIS price's amounts? Read through the store's
   *  signal (not `isUnlocked`) so the computed re-evaluates when the set
   *  changes. No store (standalone specs) reads as locked. */
  protected readonly priceAmountUnlocked = computed<boolean>(() => {
    const id = this.selectedPriceId();
    if (!id) return false;
    return this.priceLock?.unlockedIds().has(id) ?? false;
  });

  /** The gate the inputs, the handlers and the hints all share: the author
   *  may change the amounts AND has said so on this price. Everything that
   *  used to consult `mayEditPrice` alone now consults this — `mayEditPrice`
   *  survives only to tell the two locked states apart in the template
   *  ("Read-only" vs "Locked"). */
  protected readonly mayEditPriceNow = computed<boolean>(
    () => this.mayEditPrice() && this.priceAmountUnlocked(),
  );

  /** Ex-VAT amount as the input's string value — "" when the price carries
   *  none. String (not number) so an emptied field renders empty rather than
   *  snapping back to 0, matching the discount inputs below. */
  protected readonly selectedDutyFreeAmount = computed<string>(() => {
    const v = this.selectedPrice()?.dutyFreeAmount;
    return v == null ? "" : String(v);
  });

  /** Incl-VAT amount as the input's string value. This is the number the
   *  customer sees: BE writes it onto `price.value` verbatim. */
  protected readonly selectedTaxIncludedAmount = computed<string>(() => {
    const v = this.selectedPrice()?.taxIncludedAmount;
    return v == null ? "" : String(v);
  });

  /** VAT rate of the price's first tax line, as a positive percentage.
   *  `null` when the POP carries no tax line (or a zero/absent rate) — the
   *  "fill from rate" action and the drift hint both stand down there,
   *  because BE never mints a tax entry and never derives a rate. */
  protected readonly vatRatePercent = computed<number | null>(() => {
    const rate = this.selectedPrice()?.tax?.[0]?.taxRate;
    return typeof rate === "number" && Number.isFinite(rate) && rate > 0
      ? rate
      : null;
  });

  /** VAT implied by the authored pair (`incl - excl`), rounded to cents and
   *  rendered with 2 decimals. "" while either half is missing or the pair is
   *  inverted — the error line speaks for that case instead. */
  protected readonly derivedVatAmount = computed<string>(() => {
    const price = this.selectedPrice();
    const net = price?.dutyFreeAmount;
    const gross = price?.taxIncludedAmount;
    if (net == null || gross == null) return "";
    if (!Number.isFinite(net) || !Number.isFinite(gross)) return "";
    // Round away binary-float noise (105.93 - 99 → 6.930000000000007).
    const vat = Math.round((gross - net) * 100) / 100;
    return vat < 0 ? "" : vat.toFixed(2);
  });

  /** Blocking validation, mirroring the BE's `validatePriceAmounts` so the
   *  author sees the problem in the form rather than as a 400 on save. */
  protected readonly priceAmountError = computed<string>(() => {
    const price = this.selectedPrice();
    if (!price) return "";
    const net = price.dutyFreeAmount;
    const gross = price.taxIncludedAmount;
    if (net != null && (!Number.isFinite(net) || net < 0)) {
      return "Price excl. VAT must be 0 or more.";
    }
    if (gross != null && (!Number.isFinite(gross) || gross < 0)) {
      return "Price incl. VAT must be 0 or more.";
    }
    if (net != null && gross != null && net > gross) {
      return "Price excl. VAT must not exceed price incl. VAT.";
    }
    return "";
  });

  /** Non-blocking note when the authored pair doesn't match the tax line's
   *  rate. Deliberately advisory — BE saves the amounts as entered and never
   *  consults `taxRate`, so an author who means a promotional or rounded
   *  figure gets it. The cent of tolerance absorbs legitimate rounding
   *  (205.6 x 1.07 = 219.992, stored as 220), which is not drift. */
  protected readonly vatDriftHint = computed<string>(() => {
    const rate = this.vatRatePercent();
    const price = this.selectedPrice();
    const net = price?.dutyFreeAmount;
    const gross = price?.taxIncludedAmount;
    if (rate == null || net == null || gross == null) return "";
    if (this.priceAmountError()) return "";
    const expected = Math.round(net * (1 + rate / 100) * 100) / 100;
    if (Math.abs(expected - gross) <= 0.01) return "";
    return `Doesn't match ${rate}% VAT (that would be ${expected.toFixed(2)}) — saved as entered.`;
  });

  /** Which half the "fill from rate" action would compute — only offered
   *  when exactly one amount is authored and a rate exists, so it can never
   *  overwrite a figure the author put there deliberately. */
  protected readonly fillFromRateTarget = computed<
    "dutyFree" | "taxIncluded" | null
  >(() => {
    if (!this.mayEditPriceNow()) return null;
    if (this.vatRatePercent() == null) return null;
    const price = this.selectedPrice();
    if (!price) return null;
    const net = price.dutyFreeAmount;
    const gross = price.taxIncludedAmount;
    if (net != null && gross == null) return "taxIncluded";
    if (gross != null && net == null) return "dutyFree";
    return null;
  });

  protected readonly fillFromRateLabel = computed<string>(() => {
    const target = this.fillFromRateTarget();
    const rate = this.vatRatePercent();
    if (target == null || rate == null) return "";
    return target === "taxIncluded"
      ? `Fill incl. VAT from ${rate}%`
      : `Fill excl. VAT from ${rate}%`;
  });

  /** The amounts each price carried when it first loaded, captured once per
   *  id (see the constructor effect). Feeds the "leave blank to keep N" hint:
   *  an emptied field inherits the stored amount rather than clearing it, and
   *  the author needs to see which number they're inheriting. */
  private readonly loadedAmounts = signal<
    ReadonlyMap<string, { dutyFree?: number; taxIncluded?: number }>
  >(new Map());

  /** Snapshot key. Entries are scoped to the offering revision so a save that
   *  re-seeds the store with the server's response re-captures the amounts:
   *  keying on the id alone would keep advertising the pre-save figure as the
   *  one an emptied field inherits, which is exactly the lie the hint exists
   *  to prevent. */
  private amountsKey(id: string): string {
    return `${id}@${this.detail()?.updatedAt ?? ""}`;
  }

  /** The amounts the selected price arrived with this revision. */
  private storedAmounts(
    id: string,
  ): { dutyFree?: number; taxIncluded?: number } | undefined {
    return this.loadedAmounts().get(this.amountsKey(id));
  }

  /** Stored ex-VAT amount to advertise while the field is empty; "" when the
   *  price never carried one (nothing to inherit — the hint stays hidden). */
  protected readonly dutyFreeStoredHint = computed<string>(() => {
    const price = this.selectedPrice();
    if (!this.mayEditPriceNow()) return "";
    if (!price || price.dutyFreeAmount != null) return "";
    const stored = this.storedAmounts(price.id)?.dutyFree;
    return stored == null ? "" : String(stored);
  });

  protected readonly taxIncludedStoredHint = computed<string>(() => {
    const price = this.selectedPrice();
    if (!this.mayEditPriceNow()) return "";
    if (!price || price.taxIncludedAmount != null) return "";
    const stored = this.storedAmounts(price.id)?.taxIncluded;
    return stored == null ? "" : String(stored);
  });

  /** Duration-unit and persona choices for their `<enr-select>`s. Built once
   *  from the catalogues (the persona list via memoized computeds), not per
   *  change-detection pass: `enr-select` reacts to an `options` identity
   *  change (`ngOnChanges`) by rebuilding its filtered list, so a fresh array
   *  every tick would churn the open dropdown. The empty choice the native
   *  `<select>`s carried is gone — `placeholder` covers the unset state and
   *  `clearable` puts it back. */
  readonly priceDiscountDurationUnitOptions: SelectOption[] =
    PRICE_DISCOUNT_DURATION_UNITS.map((unit) => ({ value: unit, label: unit }));

  /** Personas from the master lookup (`GET /master/persona`). Until the call
   *  answers — and whenever it errors or returns nothing — the hard-coded
   *  {@link CUSTOMER_PERSONAS} fallback keeps the pricing form working. */
  private readonly loadedPersonas = toSignal(
    this.masters.getPersonas().pipe(
      map((resp) => {
        const rows: readonly PersonaRow[] = (resp?.data ?? []).filter(
          (row) => !!row?.personaType,
        );
        return rows.length > 0 ? rows : PERSONA_FALLBACK;
      }),
      catchError(() => of(PERSONA_FALLBACK)),
    ),
    { initialValue: PERSONA_FALLBACK },
  );

  /** The selectable persona rows: the loaded catalogue plus — when the
   *  selected price stores a persona the catalogue no longer carries — that
   *  stored row, so the saved selection still renders in the select and
   *  {@link onCustomerPersonaChange} can resolve its group. */
  private readonly customerPersonas = computed<readonly PersonaRow[]>(() => {
    const rows = this.loadedPersonas();
    const stored = this.selectedPrice()?.customerPersona;
    const storedType = stored?.personaType;
    if (!storedType || rows.some((row) => row.personaType === storedType)) {
      return rows;
    }
    return [
      ...rows,
      { personaGroup: stored.personaGroup, personaType: storedType },
    ];
  });

  protected readonly customerPersonaOptions = computed<SelectOption[]>(() =>
    this.customerPersonas().map(({ personaType }) => ({
      value: personaType,
      label: personaType,
    })),
  );

  protected readonly selectedCustomerPersonaType = computed(
    () => this.selectedPrice()?.customerPersona?.personaType ?? "",
  );

  protected readonly selectedPriceDiscountInput = computed(
    () => this.selectedPrice()?.priceDiscount?.priceDiscount ?? "",
  );
  protected readonly selectedPriceDiscountIncludeVatInput = computed(
    () => this.selectedPrice()?.priceDiscount?.priceDiscountIncludeVat ?? "",
  );
  protected readonly selectedPriceDiscountDurationInput = computed(
    () => this.selectedPrice()?.priceDiscount?.priceDiscountDuration ?? "",
  );
  protected readonly selectedPriceDiscountDurationUnit = computed(
    () => this.selectedPrice()?.priceDiscount?.priceDiscountDurationUnit ?? "",
  );

  protected readonly priceDiscountDurationError = computed(() => {
    const price = this.selectedPrice();
    return price ? validatePriceDiscount(price.priceDiscount) : "";
  });

  protected readonly selectedCurrencyLabel = computed<string>(() => {
    const p = this.selectedPrice();
    // Active-locale resolution so the Price-box currency follows the language
    // picker and reflects per-language currency edits (QA).
    return this.resolveActive(extractPriceUnit(p?.price)) || "";
  });

  /** Currency editor value — the price currency *is* `price.unit`
   *  (TMF Money), there is no separate `currency` field. */
  protected readonly currencyEditorValue = computed<LocalizedText>(() => {
    return extractPriceUnit(this.selectedPrice()?.price);
  });

  protected readonly taxRows = computed<TaxRow[]>(() => {
    const list = this.selectedPrice()?.tax ?? [];
    // Tax is denominated in the package currency, so the unit must mirror the
    // editable currency label (active-locale resolved, EN-first by default)
    // rather than the tax line's own catalog `taxAmount.unit`, which BE often
    // authors in a single locale only — that leaked "บาท" into the EN view.
    const currency = this.selectedCurrencyLabel();
    return list.map((t: PriceTax) => ({
      category: t.taxCategory || "Tax",
      rate: t.taxRate != null ? `${t.taxRate}%` : "—",
      amount: t.taxAmount?.value != null ? String(t.taxAmount.value) : "—",
      unit: currency || this.resolveActive(t.taxAmount?.unit) || "",
    }));
  });

  protected readonly hasTax = computed(() => this.taxRows().length > 0);
  protected readonly isTaxIncluded = computed<boolean | null>(() => {
    const v = this.selectedPrice()?.isTaxIncluded;
    return v === undefined ? null : v;
  });

  protected readonly validity = computed<PriceUnitOfMeasure>(
    () => this.selectedPrice()?.unitOfMeasure ?? {},
  );

  protected readonly billingTerm = computed<ProductOfferingTerm>(
    () => this.selectedPrice()?.productOfferingTerm?.[0] ?? {},
  );

  protected readonly billingDuration = computed<{
    amount: number | null;
    units: LocaleMap;
  }>(() => {
    const d = this.billingTerm().duration ?? {};
    return {
      amount: typeof d.amount === "number" ? d.amount : null,
      units:
        d.units && typeof d.units === "object"
          ? (d.units as unknown as LocaleMap)
          : {},
    };
  });

  /** On a child overlay whose parent already defines terms, the billing
   *  `duration.amount` is parent-owned: BE keeps the parent value and ignores a
   *  child edit (`overrideTermsKeepAmount`). Both amounts are read-only
   *  regardless; this flag only picks the badge that tells the author WHERE the
   *  value comes from (parent package vs catalog). */
  protected readonly billingAmountInherited = computed<boolean>(
    () => this.billingTerm().amountInherited === true,
  );

  /** Catalog-defined pricing model for the selected option (TMF `priceType`,
   *  e.g. "recurring" / "oneTime"), humanized for display. Read-only — the
   *  Price & currency section surfaces it so authors see how the package is
   *  charged. Empty when the catalog supplied no priceType. */
  protected readonly selectedPriceType = computed<string>(() =>
    humanizePriceType(this.selectedPrice()?.priceType),
  );

  /** True when the catalog priced this option one-time ("oneTime" / "Onetime"
   *  / "one_time" — case- and separator-insensitive). A one-time charge has no
   *  recurring period, so the whole Billing cycle section (form card + nav
   *  entry) is hidden for it. */
  protected readonly isOneTimePriceType = computed<boolean>(
    () =>
      (this.selectedPrice()?.priceType ?? "")
        .toLowerCase()
        .replaceAll(/[^a-z]/g, "") === "onetime",
  );

  /* ── Service Enrichment (displays[]) ───────────────────────────────
   *  `displayProductName` / `displayProductDescription` are kept in the
   *  array (the mobile preview reads them as the package title /
   *  description) but are no longer surfaced as editable cards — they're
   *  filtered out of the sortable list below so the UI shows only the
   *  reorderable service items. They still round-trip via `__detail`. */
  protected readonly displays = computed<EnrichmentDisplayItem[]>(
    () => this.selectedPrice()?.displays ?? [],
  );

  /** True for the generic service items that live in the sortable list and
   *  can switch between the Display / Non Display sections. Pinned
   *  title/description, both promo badges, the Upsell pop-up and the
   *  per-direction broadband speed displays all have dedicated cards and are
   *  never switchable. */
  private isSwitchableDisplay(d: EnrichmentDisplayItem): boolean {
    return (
      !NON_SERVICE_DISPLAY_NAMES.has(d.name) && !this.isBroadbandSpeedDisplay(d)
    );
  }

  protected readonly sortableDisplays = computed<
    (EnrichmentDisplayItem & { __index: number })[]
  >(() =>
    this.displays()
      .map((d, idx) => ({ ...d, __index: idx }))
      // `isRemoved` absent counts as false (shown) — BE only stamps the flag
      // once a service has been moved to Non Display at least once. Soft-
      // deleted displayOther items (`isDeleted`) are gone from BOTH sections.
      .filter(
        (d) =>
          this.isSwitchableDisplay(d) &&
          d.isRemoved !== true &&
          d.isDeleted !== true,
      ),
  );

  /** Non Display section: switchable services the user (or BE) hid via
   *  `isRemoved: true`. Same absolute `__index` convention as the sortable
   *  list, so the move/delete handlers work on either side. */
  protected readonly removedDisplays = computed<
    (EnrichmentDisplayItem & { __index: number })[]
  >(() =>
    this.displays()
      .map((d, idx) => ({ ...d, __index: idx }))
      .filter(
        (d) =>
          this.isSwitchableDisplay(d) &&
          d.isRemoved === true &&
          d.isDeleted !== true,
      ),
  );

  asLocaleMap(v: unknown): LocaleMap {
    if (v == null) return {};
    if (typeof v === "string") {
      const out: Record<string, string> = {};
      for (const lang of this.languages()) out[lang.locale] = v;
      return out as unknown as LocaleMap;
    }
    if (typeof v === "object") return v as LocaleMap;
    return {};
  }

  innerName(d: EnrichmentDisplayItem): LocaleMap {
    return this.asLocaleMap((d?.value as { name?: unknown })?.name);
  }
  innerDesc(d: EnrichmentDisplayItem): LocaleMap {
    return this.asLocaleMap(
      (d?.value as { description?: unknown })?.description,
    );
  }
  /** Supporting text (คำโปรย) for a service display — the short per-locale
   *  tagline under the name (e.g. "Internet 3 GB / day"). Lives at
   *  `value.supportingText`; the BFF stores the value object verbatim, so
   *  the key round-trips without backend mapping. */
  innerSupportingText(d: EnrichmentDisplayItem): LocaleMap {
    return this.asLocaleMap(
      (d?.value as { supportingText?: unknown })?.supportingText,
    );
  }
  /** Notes array for a display. Reads `value.notes`; for legacy data that
   *  only carries the single `value.note`, surface it as a one-item view so
   *  it stays editable. Read-only — nothing is written until the user edits,
   *  so the legacy `note` field is left untouched on disk. */
  innerNotes(d: EnrichmentDisplayItem): LocaleMap[] {
    const raw = (d?.value as { notes?: unknown })?.notes;
    if (Array.isArray(raw)) return raw.map((n) => this.asLocaleMap(n));
    const seeded = this.asLocaleMap((d?.value as { note?: unknown })?.note);
    return Object.keys(seeded).length ? [seeded] : [];
  }
  innerAmount(d: EnrichmentDisplayItem): number | null {
    const u = (d?.value as { unitOfMeasure?: { amount?: unknown } })
      ?.unitOfMeasure;
    return typeof u?.amount === "number" ? u.amount : null;
  }
  /** The figure the catalog derives for this display, as returned alongside
   *  the authored one. Shown beside the input so an author can see what they
   *  are diverging FROM — and so "Use catalog value" says what it will do.
   *  `null` when the BE sends none (an older response, or a display it does
   *  not derive), which simply hides the hint. */
  catalogAmount(d: DecoratedDisplay): number | null {
    const u = (d?.value as { unitOfMeasure?: { catalogAmount?: unknown } })
      ?.unitOfMeasure;
    return typeof u?.catalogAmount === "number" ? u.catalogAmount : null;
  }

  /** Does this display carry an amount key at all? Distinguishes a chosen
   *  Unlimited (`amount: null`) from a display that is simply tracking the
   *  catalog (no key), which look identical through {@link innerAmount}. */
  private hasAuthoredKey(d: DecoratedDisplay): boolean {
    const u = (d?.value as { unitOfMeasure?: Record<string, unknown> })
      ?.unitOfMeasure;
    return !!u && "amount" in u;
  }

  /** Where the figure on screen came from — and "we cannot tell" is one of the
   *  three answers, deliberately.
   *
   *  - `catalog`  — it matches what the backend derives, or the display holds
   *                 none of its own and will be derived on save.
   *  - `authored` — it diverges from the derived figure, so somebody chose it.
   *  - `unknown`  — the backend derives nothing for this display (no parent
   *                 bundle, a note shell, a stale ref), so there is nothing to
   *                 compare against. Claiming either of the other two here
   *                 would be a guess dressed as a fact.
   *
   *  The `authored` case uses the same comparison the save uses, so the badge
   *  can never disagree with what is about to go on the wire. */
  quantityProvenance(d: DecoratedDisplay): QuantityProvenance {
    const catalog = this.catalogAmount(d);
    if (catalog === null) return "unknown";
    if (!this.hasAuthoredKey(d)) return "catalog";
    return this.innerAmount(d) === catalog ? "catalog" : "authored";
  }

  /** Is there an authored figure that can be handed BACK to the catalog?
   *
   *  Narrower than "was this authored": it also requires a catalog figure to
   *  return to. Where the backend derives nothing, dropping the key does not
   *  restore anything — it erases the quantity, because the save replaces the
   *  display's whole value and nothing would put a number back. So the action
   *  is offered only where it is genuinely a revert. */
  quantityIsAuthored(d: DecoratedDisplay): boolean {
    return this.quantityProvenance(d) === "authored";
  }

  /** What the Amount box shows. A display with no amount key of its own is
   *  showing the catalog's figure, so render THAT rather than an empty box —
   *  otherwise handing a display back to the catalog blanks the field, which
   *  reads as "Unlimited" until a save round-trips. */
  quantityValue(d: DecoratedDisplay): string {
    if (this.hasAuthoredKey(d)) {
      const amount = this.innerAmount(d);
      return amount === null ? "" : String(amount);
    }
    const catalog = this.catalogAmount(d);
    return catalog === null ? "" : String(catalog);
  }

  /** Reading a number out of the input event, so the template needs no
   *  `$any()` cast. */
  onQuantityInput(d: DecoratedDisplay, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.onInnerAmountChange(d.__index, target?.value ?? "");
  }

  /** Has the author unlocked THIS display's Service quantity? Read through
   *  the store's signal (not `isUnlocked`) so the template re-renders when the
   *  set changes. No store (standalone specs) reads as locked. */
  quantityUnlocked(d: DecoratedDisplay): boolean {
    const id = this.selectedPriceId();
    if (!id || !d?.name) return false;
    return this.quantityLock?.unlocked().get(id)?.has(d.name) ?? false;
  }

  /** The gate the quantity input and its handlers share: the author may
   *  change Service quantities AND has said so on this display. */
  mayEditQuantityNow(d: DecoratedDisplay): boolean {
    return this.mayEditServiceQuantity() && this.quantityUnlocked(d);
  }

  /** Read the amount a display is currently showing, distinguishing a chosen
   *  Unlimited (`null`) from "never carried one" (`undefined`). */
  private currentQuantity(d: DecoratedDisplay): number | null | undefined {
    const uom = (d?.value as { unitOfMeasure?: Record<string, unknown> })
      ?.unitOfMeasure;
    if (!uom || !("amount" in uom)) return undefined;
    const amount = uom["amount"];
    return typeof amount === "number" ? amount : null;
  }

  /** Rewrite one display's `unitOfMeasure.amount`; `undefined` drops the key
   *  so the next save omits it and the backend resumes deriving the figure. */
  private writeQuantity(
    d: DecoratedDisplay,
    amount: number | null | undefined,
  ): void {
    this.updateDisplay(d.__index, (next) => {
      const value = (next.value ?? {}) as Record<string, unknown>;
      const uom = {
        ...((value["unitOfMeasure"] ?? {}) as Record<string, unknown>),
      };
      if (amount === undefined) delete uom["amount"];
      else uom["amount"] = amount;
      next.value = { ...value, unitOfMeasure: uom };
    });
  }

  /** Open this display's quantity for editing, recording what it showed so
   *  Cancel can put it back. The snapshot goes into the STORE, not a field
   *  here: this component is destroyed on every tab switch, and a snapshot
   *  that died with it would make Cancel silently no-op and re-lock on top of
   *  a typed figure the author can no longer see. */
  startQuantityEdit(d: DecoratedDisplay): void {
    const id = this.selectedPriceId();
    if (!id || !d?.name || !this.mayEditServiceQuantity()) return;
    this.quantityLock?.unlock(id, d.name, { amount: this.currentQuantity(d) });
  }

  /** Close it again, restoring what it showed at unlock — "locked" must always
   *  mean "showing what is stored", so a cancelled edit cannot leave a typed
   *  number behind a disabled input for the next save to ship. */
  cancelQuantityEdit(d: DecoratedDisplay): void {
    const id = this.selectedPriceId();
    if (!id || !d?.name) return;
    const snapshot = this.quantityLock?.snapshot(id, d.name);
    if (snapshot) this.writeQuantity(d, snapshot.amount);
    this.quantityLock?.lock(id, d.name);
  }

  /** Hand this display back to the catalog: drop the authored figure so the
   *  next save omits the key entirely and the backend resumes deriving it.
   *  The inverse of the pencil, which is why it re-locks — the display is no
   *  longer authored, so there is nothing left open to edit. */
  useCatalogQuantity(d: DecoratedDisplay): void {
    const id = this.selectedPriceId();
    // Deliberately NOT gated on the unlock: a saved override loads LOCKED, and
    // that is precisely when someone wants to hand it back. Requiring an
    // unlock first would make the pencil a toll gate on undoing. It IS gated
    // on there being a catalog figure to return to — see quantityIsAuthored.
    if (!id || !d?.name || !this.mayEditServiceQuantity()) return;
    if (!this.quantityIsAuthored(d)) return;
    this.writeQuantity(d, undefined);
    this.quantityLock?.lock(id, d.name);
  }

  innerUnit(d: EnrichmentDisplayItem): LocaleMap {
    const u = (d?.value as { unitOfMeasure?: { units?: unknown } })
      ?.unitOfMeasure;
    return this.asLocaleMap(u?.units);
  }
  innerAttachments(d: EnrichmentDisplayItem): LocalizedAttachment[] {
    const a = (d?.value as { attachment?: unknown })?.attachment;
    return Array.isArray(a) ? (a as LocalizedAttachment[]) : [];
  }

  /** True for a broadband (Fibre) display (`value.kind === "broadband"`,
   *  case-insensitive). Gates the Speed-wording editor — those fields only
   *  make sense for the display that renders the preview's Speed panel. */
  isBroadbandDisplay(d: EnrichmentDisplayItem): boolean {
    const kind = (d?.value as { kind?: unknown })?.kind;
    return typeof kind === "string" && kind.toLowerCase() === "broadband";
  }

  /** True for a per-direction broadband speed display
   *  (`value.kind === "broadbandspeed"`, case-insensitive) — the current BE
   *  shape that splits download / upload into two displays
   *  (`displayBroadbandServiceDownload` / `...Upload`). These render in the
   *  dedicated combined Broadband Speed card, not as sortable items. */
  isBroadbandSpeedDisplay(d: EnrichmentDisplayItem): boolean {
    const kind = (d?.value as { kind?: unknown })?.kind;
    return typeof kind === "string" && kind.toLowerCase() === "broadbandspeed";
  }

  /* ── Broadband Speed pair (combined card) ──────────────────────────────
   *  Surface the download + upload broadbandspeed displays as ONE card:
   *  per-direction Name, read-only Amount and Icon, with Unit / Description /
   *  Notes edited once and mirrored to both. Direction comes from the stable
   *  display KEY (`broadbandSpeedDirection`), `value.name` only as fallback. */

  /** Absolute `displays[]` indices of the download / upload broadbandspeed
   *  displays for the selected price, or null when the price carries none. */
  protected readonly broadbandSpeedPair = computed<{
    downloadIndex: number | null;
    uploadIndex: number | null;
  } | null>(() => {
    let downloadIndex: number | null = null;
    let uploadIndex: number | null = null;
    this.displays().forEach((d, idx) => {
      if (!this.isBroadbandSpeedDisplay(d)) return;
      const dir = broadbandSpeedDirection(d);
      if (dir === "upload") {
        if (uploadIndex === null) uploadIndex = idx;
      } else if (dir === "download") {
        if (downloadIndex === null) downloadIndex = idx;
      } else if (downloadIndex === null) {
        // Unknown direction → treat the first as download so the card still
        // surfaces it (unlikely given the fixed BE keys).
        downloadIndex = idx;
      }
    });
    if (downloadIndex === null && uploadIndex === null) return null;
    return { downloadIndex, uploadIndex };
  });

  /** Download-direction display (with its absolute index), or null. */
  protected readonly broadbandDownload = computed<
    (EnrichmentDisplayItem & { __index: number }) | null
  >(() => this.displayAt(this.broadbandSpeedPair()?.downloadIndex));

  /** Upload-direction display (with its absolute index), or null. */
  protected readonly broadbandUpload = computed<
    (EnrichmentDisplayItem & { __index: number }) | null
  >(() => this.displayAt(this.broadbandSpeedPair()?.uploadIndex));

  /** Display whose shared fields (Unit / Description / Notes / Icon) seed the
   *  combined card — download preferred, upload when download is absent. */
  protected readonly broadbandSeed = computed<
    (EnrichmentDisplayItem & { __index: number }) | null
  >(() => {
    const pair = this.broadbandSpeedPair();
    if (!pair) return null;
    return this.displayAt(pair.downloadIndex ?? pair.uploadIndex);
  });

  /** Resolve a display by absolute index, tagging it with `__index` so the
   *  per-direction handlers (e.g. `onInnerNameChange`) can address it. */
  private displayAt(
    index: number | null | undefined,
  ): (EnrichmentDisplayItem & { __index: number }) | null {
    if (index == null) return null;
    const d = this.displays()[index];
    return d ? { ...d, __index: index } : null;
  }

  /** Number of visible service cards in the Service enrichment section — the
   *  sortable items plus the combined Broadband Speed card (excluded from
   *  `sortableDisplays`). Drives the section header count badge so a
   *  fibre-only price doesn't read "0" beside a visible Speed card. */
  protected readonly serviceCardCount = computed<number>(
    () => this.sortableDisplays().length + (this.broadbandSpeedPair() ? 1 : 0),
  );

  /* ── Promo badge (displayProductBadge) ─────────────────────────────────
   *  A single package-level badge per price, stored as one
   *  `displayProductBadge` display item whose nested value carries the
   *  badgeKey / icon Attachment / style / priority / per-locale label
   *  (`subflix`). The label is the only i18n field; color, icon and shape
   *  are shared across locales. Read/written through the shared
   *  `badge.codec` so the Pricing tab and the Commercial Benefit tab emit
   *  an identical wire shape. */

  /** Picker data surfaced to the template. */
  protected readonly badgeTemplates: readonly EnrichmentBadgeTemplate[] =
    BADGE_TEMPLATES;
  /** Channel gate shared by the named brand palette and custom color input. */
  protected readonly isAiChatbotEditingChannel = computed<boolean>(() =>
    isAiChatbotChannel(this.channel()),
  );
  /** Badge (Detail) is a myAIS-only field — its form card and side-nav entry
   *  render (view & edit) only when editing under myAIS. An unset channel
   *  counts as myAIS (default full-field behavior), same as the
   *  Product-detail tab's gating. */
  protected readonly isMyAisEditingChannel = computed<boolean>(() =>
    isMyAisChannel(this.channel()),
  );
  protected readonly isNetworkTypeEditingChannel = computed<boolean>(() =>
    isNetworkTypeChannel(this.channel()),
  );
  /** Voice edit scope — CIVR, "IVR-CIVR", or plain IVR. Gates the
   *  CIVR-overlay sections (Voice Channel Setting, Availability, the ARPU card) IN
   *  and the voice-irrelevant merchandising Badge section
   *  OUT. IVR is its own channel but shares this form config verbatim: the
   *  fields behind the gate are plain POP characteristics the BFF writes with
   *  no channel branch. An unset channel is NOT voice, so the default pane is
   *  unchanged. */
  protected readonly isVoiceEditingChannel = computed<boolean>(() =>
    isVoiceIvrChannel(this.channel()),
  );
  /** Key Selling copy is consumed only by the IM channel. */
  protected readonly isImEditingChannel = computed<boolean>(() =>
    isImChannel(this.channel()),
  );
  protected readonly isOnlineStoreEditingChannel = computed<boolean>(() =>
    isOnlineStoreChannel(this.channel()),
  );
  /** Channel pill shown on the cards that are gated to a single edit scope.
   *  Discount and Customer persona render for AI Chatbot AND the voice
   *  channels, and the voice sections themselves are shared by the whole voice
   *  family (CIVR / IVR / IVR1175), so the label has to name the scope actually
   *  being edited — a hardcoded "AI Chatbot" (or "CIVR") would name a channel
   *  the author is not in. `voiceChannelLabel` owns that mapping so a new
   *  voice channel never needs a branch here. */
  protected readonly channelPillLabel = computed<string>(
    () => voiceChannelLabel(this.channel()) || "AI Chatbot",
  );
  /** Named background gradients for the swatch row. Base palette everywhere;
   *  editing under the AI Chatbot channel adds the AUNJAI / GOMO brand
   *  gradients. A saved color outside the visible list (e.g. a GOMO badge
   *  re-opened under myAIS) is appended so the active swatch stays visible
   *  and the selection cannot be dropped silently. Rendering is never
   *  channel-gated — badgeColorCss resolves every known type. */
  protected readonly badgeColors = computed<readonly BadgeColorType[]>(() => {
    const base = this.isAiChatbotEditingChannel()
      ? BADGE_COLOR_ORDER_AICHATBOT
      : BADGE_COLOR_ORDER;
    const current = this.badge().color;
    const isHiddenKnownType =
      current !== "" &&
      current in BADGE_COLOR_TYPES &&
      !base.includes(current as BadgeColorType);
    return isHiddenKnownType ? [...base, current as BadgeColorType] : base;
  });
  protected readonly badgeColorLabels = BADGE_COLOR_LABELS;
  protected readonly badgePositions: readonly BadgeIconPosition[] =
    BADGE_ICON_POSITIONS;

  /** CSS background for a named color type — drives the color swatches +
   *  the live preview chip. */
  protected colorCss(type: string): string {
    return badgeColorCss(type);
  }

  /** Resolve a CDN icon filename to its catalog url (empty until loaded). */
  protected resolveIconUrl(name: string): string {
    return (name && this.iconUrlByName().get(name)) || "";
  }

  /** Icon url for display — a custom upload wins, else the resolved CDN icon. */
  protected badgeIconUrl(): string {
    const b = this.badge();
    return b.iconUrl || this.resolveIconUrl(b.icon);
  }

  /** Maps the badge template's short language code to a full locale. */
  private static readonly LANG_TO_LOCALE: Record<EnrichmentLanguage, string> = {
    TH: "th-TH",
    EN: "en-US",
    MY: "my-MM",
    KH: "km-KH",
  };

  /** The selected price's promo badge, flattened for the form. Empty badge
   *  when the price carries no `displayProductBadge` item yet. */
  protected readonly badge = computed<BadgeFormValue>(() => {
    const item = this.displays().find((d) => d.name === BADGE_DISPLAY_NAME);
    return parseBadgeValue(item?.value) ?? emptyBadge();
  });

  /** Value shown by the native solid-color picker. Named gradients and an
   *  unauthored badge use black only as the picker's neutral starting point. */
  protected readonly badgeCustomColor = computed<string>(() => {
    const color = this.badge().color;
    return /^#[0-9a-f]{6}$/i.test(color) ? color : "#000000";
  });

  /** True once a `displayProductBadge` item exists on the price (so the
   *  template can show a "clear" affordance and an active-badge preview). */
  protected readonly hasBadge = computed<boolean>(() =>
    this.displays().some((d) => d.name === BADGE_DISPLAY_NAME),
  );

  /** Per-locale label map for the badge `pricing-locale-input`. */
  protected readonly badgeLabel = computed<LocaleMap>(
    () => this.badge().label as unknown as LocaleMap,
  );

  /** Live in-form badge chip — resolves the label in the active view
   *  language and pre-computes the colors so authors get the same immediate
   *  feedback as the phone preview without scanning down to it. */
  protected readonly badgePreview = computed<{
    text: string;
    icon: string;
    iconIsUrl: boolean;
    iconKeepColor: boolean;
    bg: string;
    fg: string;
    shape: string;
    iconPosition: BadgeIconPosition;
  }>(() => {
    const b = this.badge();
    const loc = this.languageMode.activeLocale();
    const text =
      b.label[loc]?.trim() ||
      b.label["th-TH"]?.trim() ||
      b.label["en-US"]?.trim() ||
      "";
    const iconUrl = b.iconUrl || this.resolveIconUrl(b.icon);
    return {
      text,
      icon: iconUrl,
      iconIsUrl: !!iconUrl,
      // Catalog icons retain their URL for persistence without becoming custom
      // uploads; only colorful catalog icons preserve their native colors.
      iconKeepColor: badgeIconKeepsColor(
        b.icon,
        b.iconUrl,
        this.iconUrlByName(),
      ),
      // Named gradient background; a legacy hex is used as-is; grey when none.
      bg: badgeColorCss(b.color) || b.color || "#f0f0f0",
      // Brand gradients are dark, so default to white text.
      fg: b.textColor || DEFAULT_BADGE_TEXT_COLOR,
      shape: b.shape || "pill",
      iconPosition: b.iconPosition || DEFAULT_BADGE_ICON_POSITION,
    };
  });

  // ── Preset dropdown ────────────────────────────────────────────────────────
  // A native <select> can't render the CDN icons in its options, so the preset
  // list is a custom cdkConnectedOverlay dropdown whose rows show the real icon.
  protected readonly badgePresetOpen = signal(false);
  protected readonly badgePresetPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 4,
    },
    {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -4,
    },
  ];
  /** The template matching the active badge, or null ("None"). */
  protected readonly selectedPreset = computed<EnrichmentBadgeTemplate | null>(
    () =>
      this.badgeTemplates.find((t) => t.id === this.badge().badgeKey) ?? null,
  );
  /** A preset's resolved CDN icon url (empty until the catalog loads). */
  protected presetIconUrl(t: EnrichmentBadgeTemplate): string {
    return this.resolveIconUrl(t.icon);
  }
  /** Whether a preset's icon keeps its own colors (vs being recolored white). */
  protected presetIconKeepColor(t: EnrichmentBadgeTemplate): boolean {
    return isColorfulIcon(t.icon);
  }
  protected toggleBadgePreset(): void {
    this.badgePresetOpen.update((v) => !v);
  }
  protected closeBadgePreset(): void {
    this.badgePresetOpen.set(false);
  }
  /** Apply a preset from the dropdown, then close it. */
  protected selectBadgePreset(id: string): void {
    this.onBadgePresetSelect(id);
    this.closeBadgePreset();
  }

  /** True when the badge chip has anything worth previewing. */
  protected readonly badgeHasPreview = computed<boolean>(() => {
    const p = this.badgePreview();
    return !!(p.text || p.icon);
  });

  /* ── Badge (Detail): second POP-level badge (displayProductBadgeDetail) ────
   *  Mirrors the promo badge above but bound to BADGE_DETAIL_DISPLAY_NAME, with
   *  its own AIS Point / Serenade-tier palette. Reuses the generic helpers
   *  (colorCss / resolveIconUrl / presetIcon* / badgePresetPositions /
   *  badgeColorLabels / badgePositions / LANG_TO_LOCALE) — only the display-name
   *  bound state is duplicated. Colors are NOT channel-gated. */
  protected readonly badgeDetailTemplates: readonly EnrichmentBadgeTemplate[] =
    BADGE_TEMPLATES_DETAIL;

  /** Named background gradients for the detail swatch row (AIS Point + tiers).
   *  A saved color outside the visible list is appended so the active swatch
   *  can never be dropped silently. */
  protected readonly badgeDetailColors = computed<readonly BadgeColorType[]>(
    () => {
      const base = BADGE_COLOR_ORDER_DETAIL;
      const current = this.badgeDetail().color;
      const isHiddenKnownType =
        current !== "" &&
        current in BADGE_COLOR_TYPES &&
        !base.includes(current as BadgeColorType);
      return isHiddenKnownType ? [...base, current as BadgeColorType] : base;
    },
  );

  protected readonly upsellBadgeColors = computed<readonly BadgeColorType[]>(
    () => {
      const base = UPSELL_BADGE_COLOR_ORDER;
      const current = this.upsellBadge().color;

      return current in BADGE_COLOR_TYPES &&
        !base.includes(current as BadgeColorType)
        ? [...base, current as BadgeColorType]
        : base;
    },
  );

  /** Upsell reuses the Hot Offers and AIS Point templates, recolored onto the
   * pop-up palette. The template id stays the `badgeKey`. */
  protected readonly upsellBadgeTemplates: readonly EnrichmentBadgeTemplate[] =
    [...BADGE_TEMPLATES, ...BADGE_TEMPLATES_DETAIL].flatMap((t) => {
      const badgeColor = UPSELL_BADGE_COLOR_BY_LIST_COLOR[t.badgeColor];
      return badgeColor ? [{ ...t, badgeColor }] : [];
    });

  protected readonly upsellBadgePresetOpen = signal(false);
  protected readonly selectedUpsellBadgePreset =
    computed<EnrichmentBadgeTemplate | null>(
      () =>
        this.upsellBadgeTemplates.find(
          (t) => t.id === this.upsellBadge().badgeKey,
        ) ?? null,
    );

  /** Upsell pop-up is its own `displayPopupUpSell` object. It deliberately
   * does not reuse the Badge Detail state: both happen to contain a badge,
   * but their labels, validity windows and save contracts are independent. */
  protected readonly upsellPopup = computed<UpsellPopupFormValue>(() => {
    const item = this.displays().find(
      (display) => display.name === UPSELL_POPUP_DISPLAY_NAME,
    );
    return parseUpsellPopupValue(item?.value);
  });

  /** Presence, not content: the parser fills defaults for a missing item. */
  protected readonly hasUpsellPopup = computed<boolean>(() =>
    this.displays().some(
      (display) => display.name === UPSELL_POPUP_DISPLAY_NAME,
    ),
  );

  protected readonly upsellName = computed<Record<string, string>>(
    () => this.upsellPopup().name,
  );

  protected readonly upsellBadge = computed<BadgeFormValue>(
    () => this.upsellPopup().badge,
  );

  protected upsellBadgeIconUrl(): string {
    const badge = this.upsellBadge();
    return badge.iconUrl || this.resolveIconUrl(badge.icon);
  }

  protected readonly upsellStartDate = computed<Date | string>(() => {
    const date = parseCivrDate(this.upsellPopup().startDateTime);
    return date ? startOfLocalDay(date) : "";
  });

  protected readonly upsellEndDate = computed<Date | string>(() => {
    const date = parseCivrDate(this.upsellPopup().endDateTime);
    return date ? startOfLocalDay(date) : "";
  });

  protected readonly upsellStartTime = computed<string>(() => {
    const date = parseCivrDate(this.upsellPopup().startDateTime);
    return date ? clockOf(date) : "";
  });

  protected readonly upsellEndTime = computed<string>(() => {
    const date = parseCivrDate(this.upsellPopup().endDateTime);
    return date ? clockOf(date) : "";
  });

  protected readonly upsellStartClocks = computed(() =>
    this.clockOptions(this.upsellStartTime()),
  );

  protected readonly upsellEndClocks = computed(() =>
    this.clockOptions(this.upsellEndTime()),
  );

  /** The selected price's detail badge, flattened for the form. Empty badge
   *  when the price carries no `displayProductBadgeDetail` item yet. */
  protected readonly badgeDetail = computed<BadgeFormValue>(() => {
    const item = this.displays().find(
      (d) => d.name === BADGE_DETAIL_DISPLAY_NAME,
    );
    return parseBadgeValue(item?.value) ?? emptyBadge();
  });

  /** Icon url for display — a custom upload wins, else the resolved CDN icon. */
  protected badgeDetailIconUrl(): string {
    const b = this.badgeDetail();
    return b.iconUrl || this.resolveIconUrl(b.icon);
  }

  /** Value shown by the native solid-color picker for the detail badge. */
  protected readonly badgeDetailCustomColor = computed<string>(() => {
    const color = this.badgeDetail().color;
    return /^#[0-9a-f]{6}$/i.test(color) ? color : "#000000";
  });

  /** True once a `displayProductBadgeDetail` item exists on the price. */
  protected readonly hasBadgeDetail = computed<boolean>(() =>
    this.displays().some((d) => d.name === BADGE_DETAIL_DISPLAY_NAME),
  );

  protected readonly collapsed = linkedSignal<
    string | undefined,
    ReadonlySet<string>
  >({
    source: () => this.selectedPrice()?.id,
    computation: () =>
      untracked(() => {
        const ids = new Set<string>();
        if (!this.hasBadge()) ids.add("section-badge");
        if (!this.hasBadgeDetail()) ids.add("section-badge-detail");
        if (!this.hasUpsellPopup()) ids.add("section-badge-upsell");
        // Catalog-owned cards start folded so the first open card in the
        // Price group is one the author can fill. Their subtitle carries
        // the figure an author would otherwise expand to read.
        ids.add("section-tax");
        if (!this.mayEditPrice()) ids.add("section-price");
        return ids;
      }),
  });

  setSectionExpanded(id: string, expanded: boolean): void {
    if (this.collapsed().has(id) !== expanded) return;
    this.collapsed.update((ids) => {
      const next = new Set(ids);
      if (expanded) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Per-locale label map for the detail badge `pricing-locale-input`. */
  protected readonly badgeDetailLabel = computed<LocaleMap>(
    () => this.badgeDetail().label as unknown as LocaleMap,
  );

  /** Live in-form detail-badge chip. */
  protected readonly badgeDetailPreview = computed<{
    text: string;
    icon: string;
    iconIsUrl: boolean;
    iconKeepColor: boolean;
    bg: string;
    fg: string;
    shape: string;
    iconPosition: BadgeIconPosition;
  }>(() => {
    const b = this.badgeDetail();
    const loc = this.languageMode.activeLocale();
    const text =
      b.label[loc]?.trim() ||
      b.label["th-TH"]?.trim() ||
      b.label["en-US"]?.trim() ||
      "";
    const iconUrl = b.iconUrl || this.resolveIconUrl(b.icon);
    return {
      text,
      icon: iconUrl,
      iconIsUrl: !!iconUrl,
      iconKeepColor: badgeIconKeepsColor(
        b.icon,
        b.iconUrl,
        this.iconUrlByName(),
      ),
      bg: badgeColorCss(b.color) || b.color || "#f0f0f0",
      // Light tier gradients carry dark ink via textColor; falls back to white.
      fg: b.textColor || DEFAULT_BADGE_TEXT_COLOR,
      shape: b.shape || "pill",
      iconPosition: b.iconPosition || DEFAULT_BADGE_ICON_POSITION,
    };
  });

  protected readonly badgeDetailPresetOpen = signal(false);
  /** The detail template matching the active badge, or null ("None"). */
  protected readonly selectedDetailPreset =
    computed<EnrichmentBadgeTemplate | null>(
      () =>
        this.badgeDetailTemplates.find(
          (t) => t.id === this.badgeDetail().badgeKey,
        ) ?? null,
    );
  protected toggleBadgeDetailPreset(): void {
    this.badgeDetailPresetOpen.update((v) => !v);
  }
  protected closeBadgeDetailPreset(): void {
    this.badgeDetailPresetOpen.set(false);
  }
  protected selectBadgeDetailPreset(id: string): void {
    this.onBadgeDetailPresetSelect(id);
    this.closeBadgeDetailPreset();
  }

  /** True when the detail-badge chip has anything worth previewing. */
  protected readonly badgeDetailHasPreview = computed<boolean>(() => {
    const p = this.badgeDetailPreview();
    return !!(p.text || p.icon);
  });

  /* Speed-panel Download / Upload wording (broadband only) — authored as
   * locale maps on the display value and read by the preview's
   * `resolveBroadbandSpeed`. Blank = fall back to the static SPEED_LABELS
   * default. (The panel heading reuses the existing Name field, so it has no
   * dedicated wording control here.) */
  innerDownloadLabel(d: EnrichmentDisplayItem): LocaleMap {
    return this.asLocaleMap(
      (d?.value as { downloadLabel?: unknown })?.downloadLabel,
    );
  }
  innerUploadLabel(d: EnrichmentDisplayItem): LocaleMap {
    return this.asLocaleMap(
      (d?.value as { uploadLabel?: unknown })?.uploadLabel,
    );
  }

  /** Blob upload prefix (container `contents`) for a service display's custom
   *  icon / logo. Custom uploads land in the shared CDN asset folders so they
   *  join the pickable catalog: icons → `iconui/iconui-custom`, logos →
   *  `ui_style/enr_image_logo_custom`. */
  displayAttachmentPrefix(suffix: "icon" | "logo"): string {
    return suffix === "logo"
      ? "enricher/assets/ui_style/enr_image_logo_custom"
      : "enricher/assets/iconui/iconui-custom";
  }

  displayTitle(d: EnrichmentDisplayItem): string {
    if (d?.name === "displayProductName") return "Product name";
    if (d?.name === "displayProductDescription") return "Product description";
    // Resolve the card title in the active view language — bare resolveLocalized
    // defaulted to Thai, so the header showed the Thai name even when EN/MM/KH
    // was selected (#50). resolveActive reads activeLocale() and re-renders on
    // a language switch.
    const inner = this.resolveActive(this.innerName(d));
    if (inner) return inner;
    return d?.name || "(unnamed display)";
  }

  trackDisplay = (_: number, d: EnrichmentDisplayItem): string =>
    `${d?.name ?? ""}::${(d as unknown as { __index?: number })?.__index ?? ""}`;

  /** packageType (lowercased) read from the POC the Product-attributes tab
   *  writes — drives prepaid-specific section visibility. */
  /** True when this package is prepaid. Read from the flat `__detail.productType`
   *  ("Prepaid" / "Postpaid") — the same field the Product-detail tab and the
   *  list use. NB: the POC literally named "packageType" is a DIFFERENT concept
   *  (Sim2Fly / Gomo), so prepaid detection must use productType. Normalised
   *  case- and separator-insensitively so "Pre-paid" / "PRE_PAID" still match. */
  protected readonly isPrepaid = computed<boolean>(
    () =>
      (this.detail()?.productType ?? "")
        .toLowerCase()
        .replaceAll(/[^a-z]/g, "") === "prepaid",
  );

  /** Editing channel — passed to the per-price Shelf & Category picker to scope
   *  its catalog feeds (`x-channel`). Protected so the template can bind
   *  `[channel]`. */
  protected readonly channel = toSignal(this.data.channel$, {
    initialValue: "" as const,
  });

  /** Per-price Shelf & Category is built but PARKED — deferred to a later
   *  release. Kept wired (component, handlers, adapter, tests) so re-enabling
   *  is just flipping this to `true`; until then the section is hidden from the
   *  side nav and the form body. */
  protected readonly showPriceShelf = false;

  /* ── Source of Fund (per-price `isSof`) ───────────────────────────────
   *  myAIS only. Prepaid: editable, on by default. Postpaid: editable and off
   *  by default on a one-time price, locked on every other price type. The
   *  table lives in `isSofRule`; the backend enforces the same one. */
  private readonly sofRule = computed(() =>
    isSofRule({
      channel: this.channel(),
      productType: this.detail()?.productType,
      priceType: this.selectedPrice()?.priceType,
    }),
  );
  protected readonly isSofVisible = computed(() => this.sofRule().visible);
  protected readonly isSofEditable = computed(() => this.sofRule().editable);
  /** The price's own value when it has one, the rule's default otherwise. The
   *  default is display only: nothing is written until the author changes it. */
  protected readonly isSofChecked = computed(() =>
    resolveIsSof(this.selectedPrice()?.isSof, this.sofRule()),
  );

  /** What the card has to tell the author beyond the toggle itself. A price
   *  that already holds a value needs no comment — the toggle shows it. The two
   *  cases that do: `"unset"`, the price has no value and the toggle is showing
   *  a default that is not in the data yet; `"pending"`, the author changed it
   *  and has not saved. A locked price with no value says nothing: there is
   *  nothing to save there. */
  protected readonly isSofStatus = computed<"unset" | "pending" | null>(() => {
    const id = this.selectedPriceId();
    if (!id || !this.isSofVisible()) return null;
    if (this.isSofEdits?.editedIds().has(id)) return "pending";
    const stored = typeof this.selectedPrice()?.isSof === "boolean";
    return !stored && this.isSofEditable() ? "unset" : null;
  });
  protected readonly isSofDefaultLabel = computed(() =>
    this.sofRule().defaultValue ? "On" : "Off",
  );

  /** Section nav. USSD No. only shows when the price carries one; Validity
   *  shows for both prepaid & postpaid; Billing cycle is hidden for prepaid
   *  (no recurring billing cycle) and for one-time prices; Badge (Detail) is myAIS-only; Ranking
   *  scores and Eligibility render on every channel, Key Selling on IM only; Shelf & Category is
   *  parked (see `showPriceShelf`). (productType) */
  protected readonly sections = computed<readonly SectionNav[]>(() =>
    SECTIONS.filter(
      (s) =>
        !(s.id === "section-ussd" && !this.selectedUssdNo()) &&
        !(s.id === "section-badge-detail" && !this.isMyAisEditingChannel()) &&
        !(s.id === "section-badge-upsell" && !this.isMyAisEditingChannel()) &&
        !(s.id === "section-sof" && !this.isSofVisible()) &&
        !(
          s.id === "section-billing" &&
          (this.isPrepaid() || this.isOneTimePriceType())
        ) &&
        !(s.id === "section-shelf" && !this.showPriceShelf) &&
        // Key Selling is IM-only; Badge has no voice surface.
        !(s.id === "section-keyselling" && !this.isImEditingChannel()) &&
        !(s.id === "section-badge" && this.isVoiceEditingChannel()) &&
        // CIVR-overlay fields — shown only under an exact CIVR edit scope.
        !(s.id === "section-civr" && !this.isVoiceEditingChannel()) &&
        !(s.id === "section-availability" && !this.isVoiceEditingChannel()) &&
        !(
          s.id === "section-online-store" && !this.isOnlineStoreEditingChannel()
        ),
    ),
  );

  protected readonly groups = computed<readonly SectionGroupView[]>(() =>
    groupSections(this.sections()),
  );

  protected readonly activeGroup = computed<SectionGroupId | undefined>(() => {
    const groups = this.groups();
    const stored = this.groupStore.activeGroup();
    return groups.some((g) => g.id === stored) ? stored : groups[0]?.id;
  });

  protected readonly activeGroupView = computed(() =>
    this.groups().find((g) => g.id === this.activeGroup()),
  );

  protected readonly adjacentGroups = computed(() => {
    const groups = this.groups();
    const i = groups.findIndex((g) => g.id === this.activeGroup());
    return { prev: groups[i - 1], next: groups[i + 1] };
  });

  /** 1-based position of the active group among the visible groups. */
  protected readonly activeGroupStep = computed(
    () => this.groups().findIndex((g) => g.id === this.activeGroup()) + 1,
  );

  /** Headline figure for the folded Price & currency card. */
  protected readonly priceSubtitle = computed<string>(() => {
    const price = this.selectedPrice();
    if (!price) return "";
    const figures = formatFigures(
      priceFigures(
        price,
        (v) => this.resolveActive(v as LocalizedText | undefined),
        this.languageMode.activeLocale(),
      ),
    );
    return figures ? `${figures} · from the catalog` : "From the catalog";
  });

  /** One-line tax summary for the folded Tax card. */
  protected readonly taxSubtitle = computed<string>(() => {
    const rows = this.taxRows();
    const flag =
      this.isTaxIncluded() === true
        ? "Tax included"
        : this.isTaxIncluded() === false
          ? "Tax excluded"
          : "";
    if (rows.length === 0) return "No tax lines on this price.";
    const lines = rows.map((t) =>
      [t.category, t.rate, [t.amount, t.unit].filter(Boolean).join(" ")]
        .filter((x) => x && x !== "—")
        .join(" "),
    );
    return [...lines, flag].filter(Boolean).join(" · ");
  });

  protected readonly sectionIssueCounts = computed<ReadonlyMap<string, number>>(
    () => {
      const issues =
        this.readiness?.byTab().get(PRICE_CONFIG_TAB)?.issues ?? [];
      const counts = new Map<string, number>();
      for (const issue of issues) {
        const section = sectionForTarget(issue);
        if (section) counts.set(section, (counts.get(section) ?? 0) + 1);
      }
      return counts;
    },
  );

  protected readonly issueCounts = computed<
    Partial<Record<SectionGroupId, number>>
  >(() => {
    const counts: Partial<Record<SectionGroupId, number>> = {};
    for (const [section, n] of this.sectionIssueCounts()) {
      const group = groupOfSection(section);
      if (group) counts[group] = (counts[group] ?? 0) + n;
    }
    return counts;
  });

  isSectionHidden(id: string): boolean {
    return groupOfSection(id) !== this.activeGroup();
  }

  /** Lands on the group's hint line, not its first card, so the line that
   *  names the group is the first thing in view. */
  selectGroup(id: SectionGroupId): void {
    this.groupStore.select(id);
    afterNextRender(
      () => {
        this.host.nativeElement
          .querySelector<HTMLElement>(".pricing__sections")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
        this.scrollLockedUntil = performance.now() + 600;
      },
      { injector: this.injector },
    );
  }

  // ── Per-price eligibility + scores ───────────────────────────────────────
  // Flat wire integers on the price (BE maps them to/from the POP's
  // serviceYears / customerAge / popularScore / marketingScore characteristics
  // — the portal never handles the TMF envelope).
  //
  // All six are TRI-STATE on the wire (see `ClearableNumber`): the key is
  // absent while the author never touched the field (BE inherits), becomes an
  // explicit `null` the moment the author empties the input (BE clears), and
  // carries the number otherwise. `toClearableNumber` — NOT a falsy test —
  // does that mapping, so a legitimate `0` survives as `0`.

  protected readonly serviceYears = computed(() => {
    const p = this.selectedPrice();
    return { min: p?.minimumServiceYears, max: p?.maximumServiceYears };
  });
  protected readonly customerAge = computed(() => {
    const p = this.selectedPrice();
    return { min: p?.minimumCustomerAge, max: p?.maximumCustomerAge };
  });
  protected readonly popularScore = computed(
    () => this.selectedPrice()?.popularScore,
  );
  protected readonly marketingScore = computed(
    () => this.selectedPrice()?.marketingScore,
  );

  protected readonly selectedRank = computed<{
    position: number;
    total: number;
    offset: number | null;
  } | null>(() => {
    const entry = this.active.entry();
    if (!entry) return null;
    return {
      position: entry.position,
      total: this.active.entries().length,
      offset: entry.offset,
    };
  });

  /** The selected price's pinned offset, or null — narrows `selectedRank` for
   *  the template's strict-equality lint rule. */
  protected readonly selectedRankOffset = computed<number | null>(
    () => this.selectedRank()?.offset ?? null,
  );

  /** Per-field eligibility error text — same rules as the legacy Product-
   *  Attributes tab: the missing bound when only one is filled, or the max
   *  field when it isn't greater than the min. */
  private eligibilityErrors(
    range: { min?: ClearableNumber; max?: ClearableNumber },
    unit: string,
  ): { min: string; max: string } {
    const hasMin = range.min != null;
    const hasMax = range.max != null;
    if (hasMin && !hasMax) return { min: "", max: `Enter maximum ${unit}.` };
    if (!hasMin && hasMax) return { min: `Enter minimum ${unit}.`, max: "" };
    if (hasMin && hasMax && (range.max as number) <= (range.min as number)) {
      return {
        min: "",
        max: `Maximum ${unit} must be greater than minimum ${unit}.`,
      };
    }
    return { min: "", max: "" };
  }

  /** Number → string for `enr-input [value]` ("" for empty). `== null` covers
   *  BOTH tri-state empties (absent `undefined` and cleared `null`); a real
   *  `0` renders as "0". */
  protected numStr(v: ClearableNumber | undefined): string {
    return v == null ? "" : String(v);
  }

  protected readonly serviceYearsErrors = computed(() =>
    this.eligibilityErrors(this.serviceYears(), "service years"),
  );
  protected readonly customerAgeErrors = computed(() =>
    this.eligibilityErrors(this.customerAge(), "customer age"),
  );

  // Each handler fires only on a REAL author edit (`enr-input` emits
  // `valueChange` from `onTextInput` / the on-blur numeric normalization, never
  // on a programmatic `[value]` write), so writing `null` here can only ever
  // mean "the author emptied this input" — a field the author never touched
  // keeps whatever the GET carried and its key stays absent = inherit.
  onServiceYearsMinChange(v: unknown): void {
    this.updateSelectedPrice((p) => {
      p.minimumServiceYears = toClearableNumber(v);
    });
  }
  onServiceYearsMaxChange(v: unknown): void {
    this.updateSelectedPrice((p) => {
      p.maximumServiceYears = toClearableNumber(v);
    });
  }
  onCustomerAgeMinChange(v: unknown): void {
    this.updateSelectedPrice((p) => {
      p.minimumCustomerAge = toClearableNumber(v);
    });
  }
  onCustomerAgeMaxChange(v: unknown): void {
    this.updateSelectedPrice((p) => {
      p.maximumCustomerAge = toClearableNumber(v);
    });
  }

  onPopularScoreChange(v: unknown): void {
    this.updateSelectedPrice((p) => {
      p.popularScore = toClearableNumber(v);
    });
  }
  onMarketingScoreChange(v: unknown): void {
    this.updateSelectedPrice((p) => {
      p.marketingScore = toClearableNumber(v);
    });
  }

  // ── CIVR overlay fields ────────────────────────────────────────────────
  // Migrated from the deleted CIVR form tab. Every clear-semantic below is
  // load-bearing and was ported verbatim — see the notes on each member.

  protected readonly civrFlagOptions = CIVR_FLAG_OPTIONS;
  protected readonly playPriceOptions = PLAY_PRICE_OPTIONS;

  /** Text → `enr-input [value]`, with `undefined` rendering as "". */
  protected civrText(value: string | undefined): string {
    return value ?? "";
  }

  /** Flag → select value. A never-authored flag DISPLAYS as No, because "No"
   *  and "nobody answered" are the same thing to every downstream reader.
   *
   *  Display only — this does NOT write. Nothing is stored until the author
   *  picks something, so merely opening a price cannot dirty the form or
   *  stamp "N" onto all three flags of every price ever saved. Same contract
   *  as the selling-window defaults above. */
  protected civrFlag(value: boolean | undefined): string {
    return value === undefined ? "false" : String(value);
  }

  /**
   * Normalise whatever `enr-input` / `enr-select` emitted into a plain string.
   * A `type="date"` input emits a Date, so a Date becomes its calendar date
   * (YYYY-MM-DD) in LOCAL terms — `toISOString()` shifts to UTC and would land
   * an ICT (+07) author on the previous day for every date they pick.
   */
  protected civrAsText(value: unknown): string {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    // A control that emitted a real boolean would otherwise fall through to
    // "" and DELETE the flag instead of setting it.
    if (typeof value === "boolean") return String(value);
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return "";
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, "0");
      const d = String(value.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    // Date[] / [string, string] are range shapes this form never asks for;
    // take the first element rather than stringifying an array.
    if (Array.isArray(value)) return this.civrAsText(value[0]);
    return "";
  }

  /**
   * Commit one clearable string field.
   *
   * The BE types these as Go `*string`: the key ABSENT means inherit, an EMPTY
   * STRING means clear. A JSON `null` would decode to a nil pointer and read as
   * absent — a silent no-op — so an emptied input must serialize as `""`.
   */
  protected setPriceText(key: PriceTextKey, value: string): void {
    this.updateSelectedPrice((p) => {
      p[key] = value.trim();
    });
  }

  protected priceText(key: PriceTextKey): string {
    return this.civrText(this.selectedPrice()?.[key]);
  }

  onOfferPackageStatusChange(v: unknown): void {
    this.setPriceText("offerPackageStatus", this.civrAsText(v));
  }
  onPatternPlayVoiceChange(v: unknown): void {
    this.setPriceText("patternPlayVoice", this.civrAsText(v));
  }
  onPatternEnsurePKChange(v: unknown): void {
    this.setPriceText("patternEnsurePK", this.civrAsText(v));
  }
  onPatternPlayMatchChange(v: unknown): void {
    this.setPriceText("patternPlayMatch", this.civrAsText(v));
  }

  /**
   * Commit one flag.
   *
   * The blank option DELETES the key rather than writing `false`. The BE has no
   * clear state for these — `false` writes "N" permanently — so emitting a
   * boolean unconditionally would turn "never authored" into "authored No" on
   * the first save of any price, for every flag the author never touched.
   */
  private setCivrFlag(
    key: "isInsurance" | "isBestSell" | "isProactive",
    value: string,
  ): void {
    this.updateSelectedPrice((p) => {
      // Defensive, not reachable from the select any more: a null/undefined
      // emission normalises to "" and must never be written as an authored
      // "No". Absent stays absent.
      if (value === CIVR_FLAG_UNSET) {
        delete p[key];
        return;
      }
      p[key] = value === "true";
    });
  }

  onIsInsuranceChange(v: unknown): void {
    this.setCivrFlag("isInsurance", this.civrAsText(v));
  }
  onIsBestSellChange(v: unknown): void {
    this.setCivrFlag("isBestSell", this.civrAsText(v));
  }
  onIsProactiveChange(v: unknown): void {
    this.setCivrFlag("isProactive", this.civrAsText(v));
  }

  /* ── Selling window, bounded by the POP `validFor` ─────────────────────
   *  The catalog's `prices[i].validFor` is the OUTER window this price may
   *  be sold in; the CIVR overlay dates carve an author-chosen window INSIDE
   *  it. So `validFor` does three jobs here:
   *    1. it SEEDS both pickers while the author has set no date yet,
   *    2. it BOUNDS the calendar through `minDate` / `maxDate`, and
   *    3. it CLAMPS a typed or programmatic value in the handlers below —
   *       the picker's own bounds only ever see a mouse pick.
   *  A price with no `validFor` (or an unparseable one) keeps the previous
   *  behaviour: no seed, no bounds, no clamp. */

  protected readonly sellingWindowMin = computed<Date | undefined>(
    () => parseCivrDate(this.selectedPrice()?.validFor?.startDateTime),
    { equal: (a, b) => sameInstant(a ?? "", b ?? "") },
  );

  protected readonly sellingWindowMax = computed<Date | undefined>(
    () => parseCivrDate(this.selectedPrice()?.validFor?.endDateTime),
    { equal: (a, b) => sameInstant(a ?? "", b ?? "") },
  );

  /** True when the price carries either bound — gates the hint that tells the
   *  author WHY the calendar refuses dates outside a range. */
  protected readonly hasSellingWindowBounds = computed<boolean>(
    () => !!this.sellingWindowMin() || !!this.sellingWindowMax(),
  );

  /** Human-readable ValidFor for that hint — the same text the
   *  pane head shows, so the two never format one window differently. */
  protected readonly sellingWindowBoundsLabel = computed<string>(
    () => this.selectedValidFor()?.label ?? "",
  );

  /** Value bound to the Start picker: the authored date when there is one,
   *  otherwise the ValidFor's start, INHERITED rather than chosen.
   *
   *  Nothing is committed to the model here — browsing prices must not dirty
   *  the form, and absent ≠ set for a differential PATCH. But the inherited
   *  value is no longer throwaway: on a voice save the coordinator seeds an
   *  unset bound from this same ValidFor (`seedSellingWindowFromValidFor`), so
   *  what this picker shows is what gets stored. That is why the parse/format
   *  helpers are shared with the save path rather than private to this file —
   *  the two must produce the same string for the same price.
   *
   *  A COMPUTED, not a template method, and reference-stable through
   *  `sameInstant`: see that helper for the change-detection loop a per-pass
   *  `Date` causes. */
  protected readonly sellingWindowStartValue = computed<Date | string>(
    () =>
      parseCivrDate(this.selectedPrice()?.effectiveStartDate) ??
      this.sellingWindowMin() ??
      "",
    { equal: sameInstant },
  );

  /** As above, defaulting to the window's end. */
  protected readonly sellingWindowEndValue = computed<Date | string>(
    () =>
      parseCivrDate(this.selectedPrice()?.effectiveEndDate) ??
      this.sellingWindowMax() ??
      "",
    { equal: sameInstant },
  );

  /* ── Date and time are edited SEPARATELY ───────────────────────────────
   *  One `datetime` picker per bound reads as one field but behaves as a
   *  two-step wizard: pick a day, press Next, then set the clock on an
   *  unlabelled hour/minute scroll wheel — ~23 notches to reach :30, and no
   *  way to type a time at all. Split, each half becomes a one-gesture
   *  control: a single-click calendar and a searchable clock list ("18" →
   *  18:00). Both halves still write the ONE `effectiveStartDate` /
   *  `effectiveEndDate` string through the same clamping handlers, so the wire
   *  shape and the POP-window rules are untouched. */

  /** Date half: midnight of the authored day, so the `date` picker never shows
   *  a clock it cannot edit. */
  protected readonly sellingWindowStartDate = computed<Date | string>(
    () => {
      const value = this.sellingWindowStartValue();
      return value instanceof Date ? startOfLocalDay(value) : "";
    },
    { equal: sameInstant },
  );

  protected readonly sellingWindowEndDate = computed<Date | string>(
    () => {
      const value = this.sellingWindowEndValue();
      return value instanceof Date ? startOfLocalDay(value) : "";
    },
    { equal: sameInstant },
  );

  /** Clock half, "HH:mm". Empty when the bound has no date at all. */
  protected readonly sellingWindowStartTime = computed<string>(() => {
    const value = this.sellingWindowStartValue();
    return value instanceof Date ? clockOf(value) : "";
  });

  protected readonly sellingWindowEndTime = computed<string>(() => {
    const value = this.sellingWindowEndValue();
    return value instanceof Date ? clockOf(value) : "";
  });

  /** The grid, plus whatever off-grid clock is already stored — a catalog
   *  window that opens at 10:07 must not be silently rounded to 10:00 just
   *  because the author opened the list. */
  private clockOptions(current: string): { label: string; value: string }[] {
    const clocks =
      !current || SELLING_WINDOW_CLOCKS.includes(current)
        ? SELLING_WINDOW_CLOCKS
        : [...SELLING_WINDOW_CLOCKS, current].sort((a, b) =>
            a.localeCompare(b),
          );
    return clocks.map((clock) => ({ label: clock, value: clock }));
  }

  protected readonly sellingWindowStartClocks = computed(() =>
    this.clockOptions(this.sellingWindowStartTime()),
  );

  protected readonly sellingWindowEndClocks = computed(() =>
    this.clockOptions(this.sellingWindowEndTime()),
  );

  /** True once either bound has a day — gates the "Full day" shortcut, which
   *  has nothing to stamp a clock onto before that. */
  protected readonly hasSellingWindowDay = computed<boolean>(
    () =>
      this.sellingWindowStartValue() instanceof Date ||
      this.sellingWindowEndValue() instanceof Date,
  );

  /* ── Is the window actually sellable? ──────────────────────────────────
   *  `clampToSellingWindow` holds each bound inside the POP window, but the
   *  two bounds are never compared against EACH OTHER — so "start 30 Jun,
   *  expire 10 Jun" saves clean and silently sells nothing. Every other
   *  date-range feature in the portal guards this (campaign-mc's
   *  `startAfterEnd`, banner, pdf-file, payment-image); the selling window
   *  was the one that did not.
   *
   *  Compared on the EFFECTIVE values — authored-or-inherited — because that
   *  is the pair the author is looking at and the pair that governs selling:
   *  an authored start pushed past an INHERITED catalog expiry is just as
   *  dead as two authored dates crossed over.
   *
   *  Advisory only. The handlers still write, so the author lands on their
   *  edit and fixes the other bound, rather than losing the keystroke — the
   *  same "clamp, never drop" contract the rest of this section keeps. */
  protected readonly sellingWindowInverted = computed<boolean>(() => {
    const start = this.sellingWindowStartValue();
    const end = this.sellingWindowEndValue();
    return (
      start instanceof Date &&
      end instanceof Date &&
      start.getTime() > end.getTime()
    );
  });

  /** Per bound: did the AUTHOR set this, or is the box showing the ValidFor?
   *  The pickers look identical either way, so without this the author cannot
   *  tell a date they chose from one they inherited.
   *
   *  Still worth drawing now that BOTH get saved — the distinction is "whose
   *  decision is this?", not "will this be stored?". The note this feeds says
   *  so in as many words; it must never call the inherited value a default
   *  again. A cleared `""` reads as inherited, which is exactly what it is:
   *  clearing a bound hands it back to the ValidFor, and the save re-seeds
   *  it from there. */
  protected readonly sellingWindowStartAuthored = computed<boolean>(
    () => !!parseCivrDate(this.selectedPrice()?.effectiveStartDate),
  );

  protected readonly sellingWindowEndAuthored = computed<boolean>(
    () => !!parseCivrDate(this.selectedPrice()?.effectiveEndDate),
  );

  /** Plain-language read-back of the window the two controls add up to.
   *  Four date/time boxes state the window in pieces; this states it as one
   *  sentence, which is what the author is actually trying to decide.
   *
   *  Deliberately NOT the lifecycle vocabulary — the header's status chip
   *  already says "Active" / "Retired" from `lifecycleStatus` on this same
   *  price, and a second chip saying "Active" about a different thing would
   *  read as the same fact twice. This talks about SELLING, not lifecycle. */
  protected readonly sellingWindowSummary = computed<string>(() => {
    if (this.sellingWindowInverted()) return "";
    const start = this.sellingWindowStartValue();
    const end = this.sellingWindowEndValue();
    const fmt = (d: Date) => formatDate(d, { pattern: "DD MMM YY HH:mm" });
    const now = Date.now();

    if (start instanceof Date && start.getTime() > now) {
      return end instanceof Date
        ? `Opens ${fmt(start)} · closes ${fmt(end)}`
        : `Opens ${fmt(start)}`;
    }
    if (end instanceof Date && end.getTime() < now) {
      return `Closed ${fmt(end)}`;
    }
    if (start instanceof Date || end instanceof Date) {
      return end instanceof Date
        ? `Sellable now · closes ${fmt(end)}`
        : "Sellable now · no closing date";
    }
    return "";
  });

  onEffectiveStartDateOnlyChange(v: unknown): void {
    this.onEffectiveStartDateChange(
      this.dayWithKeptClock(v, this.sellingWindowStartTime()),
    );
  }

  onEffectiveEndDateOnlyChange(v: unknown): void {
    this.onEffectiveEndDateChange(
      this.dayWithKeptClock(v, this.sellingWindowEndTime()),
    );
  }

  onEffectiveStartTimeChange(v: unknown): void {
    const base = this.sellingWindowStartValue();
    if (!(base instanceof Date)) return;
    this.onEffectiveStartDateChange(
      withClock(base, this.civrAsText(v) || "00:00"),
    );
  }

  onEffectiveEndTimeChange(v: unknown): void {
    const base = this.sellingWindowEndValue();
    if (!(base instanceof Date)) return;
    // Cleared falls back to 23:59, not 00:00 — this is the EXPIRY bound, its
    // placeholder promises 23:59, and "Sell whole days" closes there too.
    // 00:00 would silently shut the window at the START of the expiry day.
    this.onEffectiveEndDateChange(
      withClock(base, this.civrAsText(v) || "23:59"),
    );
  }

  /** Sell for whole days — 00:00 on the start, 23:59 on the expiry. The single
   *  most-asked-for window, and precisely the one the scroll wheel made
   *  slowest. Either bound is clamped back into the POP window afterwards, so
   *  a catalog that opens mid-morning still wins. */
  onSellingWindowFullDay(): void {
    const start = this.sellingWindowStartValue();
    if (start instanceof Date) {
      this.onEffectiveStartDateChange(withClock(start, "00:00"));
    }
    const end = this.sellingWindowEndValue();
    if (end instanceof Date) {
      this.onEffectiveEndDateChange(withClock(end, "23:59"));
    }
  }

  /** Re-attach a control's current clock to a freshly picked day. A CLEARED
   *  date clears the whole field — that is how the author drops the override
   *  and goes back to inheriting the ValidFor. */
  private dayWithKeptClock(v: unknown, clock: string): Date | string {
    if (v === null || v === undefined || v === "") return "";
    const day = v instanceof Date ? v : parseCivrDate(this.civrAsText(v));
    if (!day) return "";
    return withClock(day, clock || "00:00");
  }

  /**
   * Hold an author-entered date inside the POP window. Out-of-range values
   * are CLAMPED to the nearest bound rather than dropped, so the author lands
   * on the limit instead of losing the edit.
   */
  private clampToSellingWindow(text: string): string {
    if (!text) return text;
    const picked = parseCivrDate(text);
    if (!picked) return text;
    const min = this.sellingWindowMin();
    if (min && picked.getTime() < min.getTime()) return formatCivrDate(min);
    const max = this.sellingWindowMax();
    if (max && picked.getTime() > max.getTime()) return formatCivrDate(max);
    return text;
  }

  onEffectiveStartDateChange(v: unknown): void {
    const value = this.clampToSellingWindow(this.civrAsDateText(v));
    // Idempotent: a re-emit of the date already stored must not re-clone the
    // price and re-dirty the tab. Cheap insurance against a write/emit cycle.
    // Keyed on PRESENCE, not on `?? ""` — absent ≠ set for a differential
    // PATCH, so clearing a never-authored field still has to write "".
    if (this.civrDateUnchanged("effectiveStartDate", value)) return;
    this.updateSelectedPrice((p) => {
      p.effectiveStartDate = value;
    });
  }

  onEffectiveEndDateChange(v: unknown): void {
    const value = this.clampToSellingWindow(this.civrAsDateText(v));
    if (this.civrDateUnchanged("effectiveEndDate", value)) return;
    this.updateSelectedPrice((p) => {
      p.effectiveEndDate = value;
    });
  }

  /** True when `key` is already present on the selected price with exactly
   *  `value` — so the write would be a no-op. */
  private civrDateUnchanged(
    key: "effectiveStartDate" | "effectiveEndDate",
    value: string,
  ): boolean {
    const price = this.selectedPrice();
    return !!price && key in price && price[key] === value;
  }

  /**
   * `civrAsText`, but keeping the TIME the `datetime` picker emits. Local
   * components throughout — `toISOString()` would shift an ICT (+07) author's
   * midnight back to the previous day. A midnight pick still serialises
   * date-only, so a time-less window keeps the exact wire shape it had before
   * the picker gained a clock.
   */
  private civrAsDateText(v: unknown): string {
    if (v instanceof Date) {
      return Number.isNaN(v.getTime()) ? "" : formatCivrDate(v);
    }
    // Belt-and-braces for a ZONED string. The template pins the picker to
    // `valueFormat="date"` so it emits a Date, but a zoned ISO string reaching
    // the wire verbatim would store a UTC instant where every other reader
    // expects local text — re-anchor it to the author's own clock instead.
    if (typeof v === "string" && ZONED_ISO_PATTERN.test(v.trim())) {
      const zoned = parseCivrDate(v);
      if (zoned) return formatCivrDate(zoned);
    }
    // Range shapes this form never asks for — take the first element rather
    // than stringifying the array (mirrors `civrAsText`).
    if (Array.isArray(v)) return this.civrAsDateText(v[0]);
    return this.civrAsText(v);
  }

  /** Which amount the voicebot reads out. A KEY of the `priceDiscount` value
   *  object, so it is written and cleared with that block. */
  protected readonly selectedPlayPrice = computed<string>(
    () => this.selectedPrice()?.priceDiscount?.playPrice ?? "",
  );

  onPlayPriceChange(v: unknown): void {
    const value = this.civrAsText(v);
    this.updateSelectedPriceDiscount((discount) => {
      if (!value) delete discount.playPrice;
      else discount.playPrice = value as PriceDiscount["playPrice"];
    });
  }

  /* ── ARPU window ───────────────────────────────────────────────────────
   *  A single object characteristic, so the bounds travel together: emptying
   *  both drops the whole window. Either bound may stand alone — "ARPU 500 and
   *  up" is a real rule. */

  protected readonly arpu = computed<EnrichmentArpu>(
    () => this.selectedPrice()?.arpu ?? {},
  );

  private patchArpu(patch: (a: EnrichmentArpu) => void): void {
    this.updateSelectedPrice((p) => {
      const next: EnrichmentArpu = { ...p.arpu };
      patch(next);
      if (next.arpuFrom == null && next.arpuTo == null) {
        delete p.arpu;
        return;
      }
      p.arpu = next;
    });
  }

  onArpuFromChange(v: unknown): void {
    this.patchArpu((a) => {
      a.arpuFrom = toClearableNumber(v);
    });
  }

  onArpuToChange(v: unknown): void {
    this.patchArpu((a) => {
      a.arpuTo = toClearableNumber(v);
    });
  }

  /** Inline warning for an inverted window, so the author sees it before the
   *  BE rejects the save. Not a blocker — the field stays editable. */
  /** Inline warning for an inverted window. Phrased and placed like the
   *  eligibility range errors next door — the message renders on the MAX side
   *  of the pair, so inverted-range feedback lands on the same input across
   *  every range in this section. The comparison allows equality on purpose:
   *  a single-point ARPU window is a legitimate rule, unlike the eligibility
   *  ranges, which reject it. */
  protected readonly arpuError = computed<string>(() => {
    const { arpuFrom, arpuTo } = this.arpu();
    if (arpuFrom == null || arpuTo == null) return "";
    return arpuTo < arpuFrom
      ? "ARPU to must be greater than or equal to ARPU from."
      : "";
  });

  // ── Per-price Shelf & Category ─────────────────────────────────────────────
  /** Append a shelf onto the selected price's `shelves[]` (de-duped by id).
   *  The picker sub-component already skips already-bound ids, but the guard
   *  keeps a stray double-add harmless. */
  addPriceShelf(shelf: ShelfRef): void {
    this.updateSelectedPrice((p) => {
      const list = p.shelves ?? [];
      if (!list.some((s) => s.id === shelf.id)) p.shelves = [...list, shelf];
    });
  }

  /** Drop a shelf from the selected price's `shelves[]`. */
  removePriceShelf(id: string): void {
    this.updateSelectedPrice((p) => {
      p.shelves = (p.shelves ?? []).filter((s) => s.id !== id);
    });
  }

  /** Cascade add — bind a tree node + its whole subtree onto the selected
   *  price's `shelves[]` in one write (de-duped by id). */
  addPriceShelves(shelves: ShelfRef[]): void {
    this.updateSelectedPrice((p) => {
      const list = p.shelves ?? [];
      const seen = new Set(list.map((s) => s.id));
      const additions = shelves.filter((s) => !seen.has(s.id));
      if (additions.length) p.shelves = [...list, ...additions];
    });
  }

  /** Cascade remove — drop a tree node + its whole subtree from the selected
   *  price's `shelves[]` in one write. */
  removePriceShelves(ids: string[]): void {
    const drop = new Set(ids);
    this.updateSelectedPrice((p) => {
      p.shelves = (p.shelves ?? []).filter((s) => !drop.has(s.id));
    });
  }

  /** Active section id — drives the side-nav highlight. Updated by the
   *  scroll-spy IntersectionObserver. */
  protected readonly activeSection = signal<string>(SECTIONS[0]?.id ?? "");

  private readonly packageDrawerTemplate = viewChild.required<
    TemplateRef<unknown>
  >("packageDrawerTemplate");
  private sectionObserver?: IntersectionObserver;
  protected readonly sectionAir = `${SECTION_AIR_PX}px`;

  constructor() {
    effect(() => {
      const focus = this.focus();
      if (focus && normalizeTabId(focus.tabId) === "price-config") {
        untracked(() => this.applyFocus(focus));
      }
    });

    // Group switches and focus jumps never pass through a section card, so
    // the pointer/focus listener alone would leave the pop-up open over a
    // preview whose Upsell card is no longer on screen.
    effect(() => {
      const shown =
        this.sections().some((s) => s.id === "section-badge-upsell") &&
        !this.isSectionHidden("section-badge-upsell");
      if (!shown)
        untracked(() => this.previewSpotlight.upsellPopupOpen.set(false));
    });

    // `relatedPrice` persists only an id. Resolve its display data whenever
    // the selected tier's Upsell popup changes, so a saved relation renders
    // the same package card after a page reload. No id means no request and
    // leaves `selectedPackage` empty, which exposes the existing Add Package
    // affordance in the template.
    effect((onCleanup) => {
      const relatedPriceId = this.upsellPopup().relatedPriceId.trim();
      if (!relatedPriceId) {
        if (this.selectedPackage() !== null) this.selectedPackage.set(null);
        return;
      }

      if (this.selectedPackage()?.id === relatedPriceId) return;
      this.selectedPackage.set(null);

      const request = this.enrichmentService
        .getEnrichmentPriceById(relatedPriceId, {
          ...ENRICHMENT_HEADER_TRANSPORT,
        })
        .subscribe({
          next: (response) => {
            // A selection/tab change can happen while the request is in
            // flight. Never write an old package into the newly selected tier.
            if (this.upsellPopup().relatedPriceId.trim() !== relatedPriceId) {
              return;
            }
            const packageItem = response.data;
            this.selectedPackage.set(
              packageItem ? this.toSelectedUpsellPackage(packageItem) : null,
            );
          },
          error: () => {
            if (this.upsellPopup().relatedPriceId.trim() === relatedPriceId) {
              this.selectedPackage.set(null);
            }
          },
        });

      onCleanup(() => request.unsubscribe());
    });

    // Capture each price's amounts as they FIRST arrive at a given offering
    // revision, for the "leave blank to keep N" hint (see `loadedAmounts`).
    // `untracked` on the accumulator keeps the effect from depending on its
    // own write; it re-fires whenever the price list changes, but a key
    // already captured is never revisited, so editing an amount can't
    // overwrite the snapshot — while a save (new `updatedAt`) re-keys and
    // therefore re-captures.
    effect(() => {
      const list = this.prices();
      const rev = this.detail()?.updatedAt ?? "";
      const known = untracked(this.loadedAmounts);
      let added: Map<
        string,
        { dutyFree?: number; taxIncluded?: number }
      > | null = null;
      for (const p of list) {
        const key = `${p.id}@${rev}`;
        if (known.has(key)) continue;
        (added ??= new Map(known)).set(key, {
          dutyFree: p.dutyFreeAmount,
          taxIncluded: p.taxIncludedAmount,
        });
      }
      if (added) this.loadedAmounts.set(added);
    });

    effect(() => {
      this.activeGroup();
      untracked(() => {
        const group = this.activeGroupView();
        if (group) this.activeSection.set(group.sections[0].id);
        afterNextRender(() => this.observeSections(), {
          injector: this.injector,
        });
      });
    });

    afterNextRender(() => this.bindSectionObserver());
  }

  private bindSectionObserver(): void {
    if (!("IntersectionObserver" in window)) return;
    this.sectionObserver?.disconnect();
    this.sectionObserver = new IntersectionObserver(
      // The observer only delivers DELTA entries (sections that just
      // crossed a threshold), not the full set. Picking the active
      // section from delta alone gives wrong answers — e.g. when
      // "Product description" first enters view, it's the only entry,
      // so it wins even though "Product name" is still visible above
      // it. We re-evaluate every observed section fresh via
      // getBoundingClientRect() on every callback instead.
      () => this.recomputeActiveSection(),
      {
        rootMargin: `-${FORM_VIEWPORT_TOP_PX}px 0px ${SPY_ACTIVE_BAND_BOTTOM} 0px`,
        threshold: [0, 0.5, 1],
      },
    );
    queueMicrotask(() => this.observeSections());
  }

  private recomputeActiveSection(): void {
    if (performance.now() < this.scrollLockedUntil) return;
    const root = this.host.nativeElement;
    interface Candidate {
      id: string;
      top: number;
    }
    const candidates: Candidate[] = [];
    for (const s of this.activeGroupView()?.sections ?? []) {
      const el = root.querySelector<HTMLElement>(`#${s.id}`);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (rect.bottom <= FORM_VIEWPORT_TOP_PX) continue;
      candidates.push({ id: s.id, top: rect.top });
    }
    if (candidates.length === 0) return;
    candidates.sort((a, b) => a.top - b.top);
    const next =
      candidates.find(
        (c) => c.top >= FORM_VIEWPORT_TOP_PX - SPY_BOUNDARY_SLACK_PX,
      )?.id ?? candidates[0].id;
    if (next !== this.activeSection()) this.activeSection.set(next);
  }

  private observeSections(): void {
    const observer = this.sectionObserver;
    if (!observer) return;
    observer.disconnect();
    const root = this.host.nativeElement;
    for (const s of this.activeGroupView()?.sections ?? []) {
      const el = root.querySelector<HTMLElement>(`#${s.id}`);
      if (el) observer.observe(el);
    }
  }

  ngOnDestroy(): void {
    this.previewSpotlight.upsellPopupOpen.set(false);
    this.sectionObserver?.disconnect();
    if (this.appLinkCopiedTimer) clearTimeout(this.appLinkCopiedTimer);
  }

  /** Editing inside the Upsell section opens its pop-up in the preview;
   *  editing any other form section closes it. The card's own toggle is
   *  skipped so its click alone decides, and clicks outside the section
   *  cards (tier strip, side nav) leave it as is. */
  @HostListener("focusin", ["$event.target"])
  @HostListener("pointerdown", ["$event.target"])
  protected syncUpsellPopupPreview(target: EventTarget | null): void {
    if (!(target instanceof Element)) return;
    if (target.closest("[data-upsell-preview-toggle]")) return;
    if (target.closest("#section-badge-upsell")) {
      this.previewSpotlight.upsellPopupOpen.set(true);
    } else if (target.closest('[id^="section-"]')) {
      this.previewSpotlight.upsellPopupOpen.set(false);
    }
  }

  protected toggleUpsellPopupPreview(): void {
    this.previewSpotlight.upsellPopupOpen.update((open) => !open);
  }

  openPackageDrawer(): void {
    const drawerService = this.injector.get(OmniarkDrawerService, null);
    if (!drawerService) return;
    drawerService.showDrawerRef(this.packageDrawerTemplate(), {
      width: "60%",
      position: "right",
      backdropDismiss: true,
      showCloseButton: false,
    });
  }

  onPackageSelected(item: EnrichmentListPriceItem): void {
    this.selectedPackage.set(item);
    this.updateUpsellPopup((popup) => {
      popup.relatedPriceId = item.id;
    });
  }

  removeSelectedPackage(): void {
    this.selectedPackage.set(null);
    this.updateUpsellPopup((popup) => {
      popup.relatedPriceId = "";
    });
  }

  /** The detail endpoint returns an editor-grade price while the selected
   * package card uses the lightweight list-row shape. Project only the card's
   * fields; the stored relation remains the authoritative POP id. */
  private toSelectedUpsellPackage(
    price: EnrichmentPriceItem,
  ): EnrichmentListPriceItem {
    const value = extractPriceAmount(price.price);
    const unit = this.resolveActive(extractPriceUnit(price.price)).trim();

    return {
      id: price.id,
      name:
        this.resolveActive(price.systemName).trim() ||
        this.resolveActive(price.name).trim(),
      price:
        value === null
          ? undefined
          : {
              value,
              ...(unit ? { unit } : {}),
            },
      priceType: price.priceType,
      productCode: price.productCode,
      descriptionShort: price.descriptionShort,
    };
  }

  protected packagePriceLabel(item: EnrichmentListPriceItem): string {
    const value = item.price?.value;
    if (value === undefined || value === null) return "-";
    const unit = item.price?.unit?.trim().toLowerCase();
    if (unit === "baht" || unit === "thb") return `${value.toLocaleString()} ฿`;
    return unit ? `${value.toLocaleString()} ${unit}` : value.toLocaleString();
  }

  protected packageMeta(item: EnrichmentListPriceItem): string {
    return [item.offering?.productType, item.offering?.offerClassificationName]
      .filter((value): value is string => Boolean(value))
      .join(" · ");
  }

  private applyFocus(focus: FieldFocus): void {
    const displayName = this.displayNameFromFieldKey(focus.fieldKey);
    if (displayName) this.expandedDisplay.set(displayName);
    const section = sectionForTarget(focus);
    if (section) {
      const group = groupOfSection(section);
      if (group) this.groupStore.select(group);
      this.setSectionExpanded(section, true);
      afterNextRender(() => this.selectSection(section), {
        injector: this.injector,
      });
    }
  }

  /** Extract a service-display name from a readiness fieldKey
   *  (`displays.<name>.value.name`); null for any other field. */
  private displayNameFromFieldKey(fieldKey?: string): string | null {
    if (!fieldKey) return null;
    const match = /^displays\.(.+)\.value\.name$/.exec(fieldKey);
    return match ? match[1] : null;
  }

  /** Smooth-scroll to a form section and lock the highlight on it for
   *  ~600ms. Without this lock the IntersectionObserver fires mid-scroll
   *  and recomputes a "more correct" candidate (often the section just
   *  *below* the one we're scrolling to) — which makes the highlight
   *  bounce. The lock is just a recompute-suppress window. */
  selectSection(id: string): void {
    const group = groupOfSection(id);
    if (group && group !== this.activeGroup()) {
      this.groupStore.select(group);
      afterNextRender(() => this.selectSection(id), {
        injector: this.injector,
      });
      return;
    }
    const el = this.host.nativeElement.querySelector<HTMLElement>(`#${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    this.activeSection.set(id);
    this.scrollLockedUntil = performance.now() + 600;
  }

  /** Suppress IntersectionObserver-driven highlight updates until this
   *  timestamp (set by `selectSection`). */
  private scrollLockedUntil = 0;

  trackTaxRow = (i: number, _: TaxRow): number => i;

  sectionNumber(id: string): number {
    const sections =
      this.groups().find((g) => g.id === groupOfSection(id))?.sections ?? [];
    return sections.findIndex((s) => s.id === id) + 1;
  }

  /* ── Price Classification ───────────────────────────────────────────── */

  /** The selected price's own classification (`null` = inherits). */
  protected readonly selectedPriceClassification =
    computed<PriceClassificationValues>(() => {
      const p = this.selectedPrice();
      return {
        networkType: p?.networkType ?? null,
        segment: p?.segment ?? null,
        packageType: p?.packageType ?? null,
        productEffectiveOption: p?.productEffectiveOption ?? null,
      };
    });

  /** The offering-level values (Product attributes tab) — the hint source. */
  protected readonly offeringClassification =
    computed<PriceClassificationValues>(() => {
      const d = this.detail();
      return {
        networkType: d?.networkType ?? null,
        segment: d?.segment ?? null,
        packageType: d?.packageType ?? null,
        productEffectiveOption: d?.productEffectiveOption ?? null,
      };
    });

  onPriceClassificationChange(change: PriceClassificationChange): void {
    this.updateSelectedPrice((p) => {
      p[change.field] = [...change.values];
    });
  }

  /* ── Price Agreement ────────────────────────────────────────────────── */

  /** The Offering Agreement (Term tab edits index 0). */
  protected readonly offeringAgreement = computed<DetailAgreementRef | null>(
    () => this.detail()?.agreements?.find((a) => !!a?.id) ?? null,
  );

  protected readonly agreementUsage = computed<AgreementUsage | null>(() => {
    const offering = this.offeringAgreement();
    const inEffect = (p: EnrichmentPriceItem | null) =>
      p ? effectiveAgreement(p.agreements, offering)?.id : undefined;
    const id = inEffect(this.selectedPrice());
    if (!id) return null;
    const prices = this.prices();
    return {
      count: prices.filter((p) => inEffect(p) === id).length,
      total: prices.length,
    };
  });

  /** `[]` = the price follows the offering again (the BFF removes its own). */
  onPriceAgreementChange(agreements: DetailAgreementRef[]): void {
    this.updateSelectedPrice((p) => {
      p.agreements = agreements.map((a) => ({ ...a }));
    });
  }

  /* ── Write-back ─────────────────────────────────────────────────────── */

  private updateSelectedPrice(
    patch: (flat: EnrichmentPriceItem) => void,
  ): void {
    const m = this.model();
    const id = this.selectedPriceId();
    if (!m || !id || !isFlatEnrichmentModel(m)) return;

    const prices = m.__detail.prices ?? [];
    const idx = prices.findIndex((p) => p.id === id);
    if (idx < 0) return;

    // Deep-clone ONLY the edited price — not the whole model. This was the
    // dominant per-keystroke allocation (the full model carries every price's
    // displays/tax/shelves plus the duplicated POP mirror). Untouched prices +
    // POPs ride by reference; the enclosing arrays/objects are shallow-rebuilt
    // so the model is still a fresh immutable reference for signal consumers.
    const flat = structuredClone(prices[idx]) as EnrichmentPriceItem;
    patch(flat);
    const nextPrices = prices.slice();
    nextPrices[idx] = flat;

    // Mirror the edit onto the matching legacy POP so the mobile preview (which
    // reads `pop.name/price/unitOfMeasure/productOfferingTerm/__displays`)
    // reflects it immediately. Clone only that one POP.
    let nextPOPs = m.productOfferingPrice;
    const pops = m.productOfferingPrice;
    const pIdx = pops?.findIndex((p) => p.id === id) ?? -1;
    if (pops && pIdx >= 0) {
      const pop = structuredClone(pops[pIdx]) as FlatPOP;
      pop.name = resolveLocalized(flat.name);
      pop.description = resolveLocalized(flat.description) || undefined;
      // Keep the LocaleMap mirrors fresh so the preview's per-locale
      // resolver (NOT the default-locale string above) reflects edits.
      pop.__name = flat.name;
      pop.__description = flat.description;
      const amt = extractPriceAmount(flat.price);
      pop.price = {
        value: amt ?? undefined,
        unit: (extractPriceUnit(flat.price) ?? pop.price?.unit) as never,
      };
      // Authored amount pair + the tax line the local derive rewrites. Without
      // the `tax` mirror the preview would keep the pre-edit VAT figure, and
      // without the pair it would re-derive net/gross from `price.value` +
      // `isTaxIncluded` — which disagrees once these are authored, since BE
      // makes `price.value` incl-VAT regardless of that flag.
      pop.dutyFreeAmount = flat.dutyFreeAmount;
      pop.taxIncludedAmount = flat.taxIncludedAmount;
      if (flat.tax !== undefined) {
        pop.tax = flat.tax as never;
      }
      if (flat.unitOfMeasure !== undefined) {
        pop.unitOfMeasure = flat.unitOfMeasure as never;
      }
      if (flat.productOfferingTerm !== undefined) {
        pop.productOfferingTerm = flat.productOfferingTerm as never;
      }
      if (flat.displays !== undefined) pop.__displays = flat.displays;
      nextPOPs = pops.slice();
      nextPOPs[pIdx] = pop;
    }

    const next: FlatEnrichmentModel = {
      ...m,
      __detail: { ...m.__detail, prices: nextPrices },
      productOfferingPrice: nextPOPs,
    };
    this.data.setEnrichment(next);
    this.priceDirty?.markDirty(id);
    this.sectionDirty?.markTabDirty("price-config");
  }

  /* Per-field edit adapters — each commits a single locale-map / value. */

  /** Source of Fund toggle. The UI kit's `change` fires twice per click — the
   *  boolean, then the bubbled DOM event — so only a boolean counts, and it is
   *  SET, never flipped. Marking the price in `isSofEdits` is what lets the
   *  value onto the wire; an untouched price never sends one. */
  onIsSofChange(value: boolean | Event): void {
    if (typeof value !== "boolean" || !this.isSofEditable()) return;
    const id = this.selectedPriceId();
    if (!id) return;
    this.updateSelectedPrice((p) => {
      p.isSof = value;
    });
    this.isSofEdits?.markEdited(id);
  }

  onNameChange(value: LocaleMap): void {
    this.updateSelectedPrice((p) => {
      p.name = value;
    });
  }

  onDescriptionChange(value: LocaleMap): void {
    this.updateSelectedPrice((p) => {
      p.description = value;
    });
  }

  /** keySelling for the textarea: per-locale value with the wire "|" expanded
   *  to real newlines. */
  protected readonly keySellingText = computed<LocaleMap>(() =>
    pipesToNewlines(this.selectedPrice()?.keySelling),
  );

  onKeySellingChange(value: LocaleMap): void {
    this.updateSelectedPrice((p) => {
      p.keySelling = newlinesToPipes(value);
    });
  }

  /** Commit an edited currency. The currency is the TMF Money
   *  `price.unit` — write it there, preserving any existing amount.
   *  Also mirror onto every `tax[].taxAmount.unit` so tax lines stay in
   *  the same currency as the price. */
  onCurrencyChange(value: LocaleMap): void {
    this.updateSelectedPrice((p) => {
      const amount = extractPriceAmount(p.price);
      p.price = { value: amount ?? undefined, unit: value };
      if (Array.isArray(p.tax) && p.tax.length > 0) {
        p.tax = p.tax.map((t) => ({
          ...t,
          taxAmount: { ...(t.taxAmount ?? {}), unit: value },
        }));
      }
    });
  }

  /** Local mirror of the BE's `deriveAmountPriceTax` (BFF
   *  `internal/enrichment/price_amount.go`), so the Tax card and the mobile
   *  preview show what the save will actually produce:
   *
   *      price.value            = taxIncludedAmount
   *      tax[0].taxAmount.value = taxIncludedAmount - dutyFreeAmount
   *
   *  Follows the BE's restraint exactly — nothing is minted when the price
   *  carries no tax line, only `tax[0]` is touched, and `taxRate` /
   *  `taxCategory` are never derived. The price currency (`price.unit`) is
   *  preserved; an inverted pair is left alone because `priceAmountError`
   *  already blocks it and BE would reject the save. */
  private applyDerivedAmounts(price: EnrichmentPriceItem): void {
    // An omitted amount resolves to the stored one — the same rule the BE
    // applies (`deriveAmountPriceTax` reads the already-written char list), so
    // emptying one field re-derives against what the save will actually
    // inherit instead of leaving the last typed figure on `price.value`.
    const stored = this.storedAmounts(price.id);
    const gross = price.taxIncludedAmount ?? stored?.taxIncluded;
    if (gross == null || !Number.isFinite(gross)) return;
    price.price = { value: gross, unit: extractPriceUnit(price.price) };

    const net = price.dutyFreeAmount ?? stored?.dutyFree;
    if (net == null || !Number.isFinite(net) || net > gross) return;
    if (!Array.isArray(price.tax) || price.tax[0]?.taxAmount == null) return;
    // Round away binary-float noise, matching `costCodePriceInclVat`.
    const vat = Math.round((gross - net) * 100) / 100;
    price.tax = price.tax.map((t, i) =>
      i === 0 ? { ...t, taxAmount: { ...t.taxAmount, value: vat } } : t,
    );
  }

  /** Open the amounts for editing on the selected price (the pencil).
   *  No-op without the permission — the button is not rendered then, and a
   *  second guard here keeps the store honest. */
  startPriceAmountEdit(): void {
    if (!this.mayEditPrice()) return;
    const id = this.selectedPriceId();
    if (id) this.priceLock?.unlock(id);
  }

  /** Close the amounts again, discarding whatever was typed.
   *
   *  Locking means "showing what is stored", so it reverts to the snapshot
   *  the price loaded with this revision rather than leaving an edit behind
   *  a disabled input — an author who locks a card and then saves must not
   *  ship a figure the card is no longer showing them. Restores the absent
   *  case too: an amount the price never carried goes back to absent
   *  ("inherit"), not to a zero.
   *
   *  `loadedAmounts` re-keys on the offering's `updatedAt`, so after a save
   *  the snapshot is the freshly stored pair, not the pre-save one. */
  cancelPriceAmountEdit(): void {
    const id = this.selectedPriceId();
    if (!id) return;
    const stored = this.storedAmounts(id);
    this.updateSelectedPrice((price) => {
      if (stored?.dutyFree == null) delete price.dutyFreeAmount;
      else price.dutyFreeAmount = stored.dutyFree;
      if (stored?.taxIncluded == null) delete price.taxIncludedAmount;
      else price.taxIncludedAmount = stored.taxIncluded;
      this.applyDerivedAmounts(price);
    });
    this.priceLock?.lock(id);
  }

  /** Write the authored ex-VAT amount. An emptied input drops the key, which
   *  the BE reads as "inherit the stored characteristic" — it does NOT clear
   *  the amount (there is no clear on this contract), which is what the
   *  card's `dutyFreeStoredHint` tells the author. */
  onDutyFreeAmountChange(raw: string): void {
    if (!this.mayEditPriceNow()) return;
    const value = optionalFiniteNumber(raw);
    this.updateSelectedPrice((price) => {
      if (value === undefined) delete price.dutyFreeAmount;
      else price.dutyFreeAmount = value;
      this.applyDerivedAmounts(price);
    });
  }

  /** Write the authored incl-VAT amount — the figure the customer sees, since
   *  BE copies it onto `price.value`. Same inherit-on-empty semantics as
   *  {@link onDutyFreeAmountChange}. */
  onTaxIncludedAmountChange(raw: string): void {
    if (!this.mayEditPriceNow()) return;
    const value = optionalFiniteNumber(raw);
    this.updateSelectedPrice((price) => {
      if (value === undefined) delete price.taxIncludedAmount;
      else price.taxIncludedAmount = value;
      this.applyDerivedAmounts(price);
    });
  }

  /** One-shot: compute the missing half of the pair from the tax line's rate.
   *  Deliberately not a live two-way binding — catalog pairs are not always
   *  rate-exact, and silently recomputing would clobber a figure the author
   *  entered on purpose. No-op unless exactly one amount is authored. */
  fillFromVatRate(): void {
    if (!this.mayEditPriceNow()) return;
    const target = this.fillFromRateTarget();
    const rate = this.vatRatePercent();
    if (target == null || rate == null) return;
    this.updateSelectedPrice((price) => {
      if (target === "taxIncluded") {
        const net = price.dutyFreeAmount;
        if (net == null) return;
        price.taxIncludedAmount =
          Math.round(net * (1 + rate / 100) * 100) / 100;
      } else {
        const gross = price.taxIncludedAmount;
        if (gross == null) return;
        price.dutyFreeAmount =
          Math.round((gross / (1 + rate / 100)) * 100) / 100;
      }
      this.applyDerivedAmounts(price);
    });
  }

  /** Patches the selected price's nested discount block, dropping the block
   *  outright once every key has been cleared — the BE reads an absent (or
   *  empty) `priceDiscount` as "remove the stored discount", so an empty
   *  husk would only be noise on the wire. */
  private updateSelectedPriceDiscount(
    patch: (discount: PriceDiscount) => void,
  ): void {
    this.updateSelectedPrice((price) => {
      const discount: PriceDiscount = { ...price.priceDiscount };
      patch(discount);
      if (Object.keys(discount).length === 0) delete price.priceDiscount;
      else price.priceDiscount = discount;
    });
  }

  onPriceDiscountChange(raw: string): void {
    const value = optionalFiniteNumber(raw);
    this.updateSelectedPriceDiscount((discount) => {
      if (value === undefined) delete discount.priceDiscount;
      else discount.priceDiscount = value;
    });
  }

  onPriceDiscountIncludeVatChange(raw: string): void {
    const value = optionalFiniteNumber(raw);
    this.updateSelectedPriceDiscount((discount) => {
      if (value === undefined) delete discount.priceDiscountIncludeVat;
      else discount.priceDiscountIncludeVat = value;
    });
  }

  onPriceDiscountDurationChange(raw: string): void {
    const value = optionalFiniteNumber(raw);
    this.updateSelectedPriceDiscount((discount) => {
      if (value === undefined) delete discount.priceDiscountDuration;
      else discount.priceDiscountDuration = value;
    });
  }

  onPriceDiscountDurationUnitChange(raw: string | null): void {
    const unit = PRICE_DISCOUNT_DURATION_UNITS.find((value) => value === raw);
    this.updateSelectedPriceDiscount((discount) => {
      if (!unit) delete discount.priceDiscountDurationUnit;
      else discount.priceDiscountDurationUnit = unit;
    });
  }

  /** Writes the persona picked in the select. The catalogue row is the source
   *  of both keys — the template only ever hands back a personaType — so the
   *  stored pair can never drift. Resolution goes through the loaded list
   *  (including a merged-in stored row, see {@link customerPersonas}), not the
   *  offline fallback. An empty or unknown value drops the block: the BE reads
   *  an absent `customerPersona` as "remove the stored persona" — which is
   *  what the select's clear button emits (`null`). */
  onCustomerPersonaChange(raw: string | null): void {
    const row = this.customerPersonas().find((p) => p.personaType === raw);
    this.updateSelectedPrice((price) => {
      if (!row) {
        delete price.customerPersona;
        return;
      }
      price.customerPersona = {
        personaGroup: row.personaGroup,
        personaType: row.personaType,
      };
    });
  }

  onValidityUnitsChange(value: LocaleMap): void {
    this.updateSelectedPrice((p) => {
      p.unitOfMeasure = { ...(p.unitOfMeasure ?? {}), units: value };
    });
  }

  /** Writes the Validity amount (`pop.unitOfMeasure.amount`). A cleared /
   *  non-numeric input drops the key — the BFF reads an absent (or zero)
   *  amount as "not authored" and keeps inheriting the parent catalog's
   *  amount, mirroring the Units fallback semantics. */
  onValidityAmountChange(raw: string): void {
    const value = optionalFiniteNumber(raw);
    this.updateSelectedPrice((p) => {
      const next = { ...(p.unitOfMeasure ?? {}) };
      if (value === undefined) delete next.amount;
      else next.amount = value;
      p.unitOfMeasure = next;
    });
  }

  onBillingUnitsChange(value: LocaleMap): void {
    this.updateSelectedPrice((p) => {
      const terms = p.productOfferingTerm ?? [];
      const first: ProductOfferingTerm = terms[0] ?? {};
      const nextTerm: ProductOfferingTerm = {
        ...first,
        duration: {
          ...(first.duration ?? {}),
          units: value as unknown as string,
        },
      };
      p.productOfferingTerm = [nextTerm, ...terms.slice(1)];
    });
  }

  /** Commit an edited attachment list for a single display. Spreads the
   *  original `value` first so untouched fields (`kind`, `characteristics`,
   *  `productOfferingRef`, `name`, `description`, `note`, `unitOfMeasure`)
   *  survive — only `attachment` is overlaid. */
  onDisplayAttachmentsChange(index: number, next: LocalizedAttachment[]): void {
    this.updateSelectedPrice((p) => {
      const list = [...(p.displays ?? [])];
      const target = list[index];
      if (!target) return; // display already removed — skip
      const originalValue =
        target.value && typeof target.value === "object"
          ? (target.value as Record<string, unknown>)
          : {};
      list[index] = {
        ...target,
        value: { ...originalValue, attachment: next },
      };
      p.displays = list;
    });
  }

  /* ── Promo badge write-back ─────────────────────────────────────────────
   *  All mutations route through `updateBadge`, which finds (or creates) the
   *  single `displayProductBadge` item on the price, applies the patch to the
   *  flattened form value, then re-serialises via the shared codec — seeding
   *  `subflix` from the prior on-disk value so locales the user isn't editing
   *  survive. Editing every field back to blank drops the item so the price
   *  never round-trips an empty badge envelope. The item carries
   *  `valueType: "object"` so the backend stamps the right TMF `@type`. */
  private updateBadge(patch: (b: BadgeFormValue) => void): void {
    this.updateSelectedPrice((p) => {
      const list = [...(p.displays ?? [])];
      const idx = list.findIndex((d) => d.name === BADGE_DISPLAY_NAME);
      const priorValue = idx >= 0 ? list[idx].value : undefined;
      const current = parseBadgeValue(priorValue) ?? emptyBadge();
      patch(current);

      if (isBadgeEmpty(current)) {
        if (idx >= 0) {
          list.splice(idx, 1);
          p.displays = list;
        }
        return;
      }

      const item: EnrichmentDisplayItem = {
        name: BADGE_DISPLAY_NAME,
        valueType: "object",
        value: buildBadgeValue(current, priorValue),
      };
      if (idx >= 0) list[idx] = item;
      else list.push(item);
      p.displays = list;
    });
  }

  /** Apply a preset template — overlays badgeKey, colors, shape, icon and the
   *  per-locale label in one commit. The blank "none" preset clears instead. */
  /** Apply a preset chosen from the Preset select; an empty value clears it. */
  onBadgePresetSelect(id: string): void {
    if (!id) {
      this.clearBadge();
      return;
    }
    const t = this.badgeTemplates.find((x) => x.id === id);
    if (t) this.applyBadgeTemplate(t);
  }

  applyBadgeTemplate(t: EnrichmentBadgeTemplate): void {
    this.updateBadge((b) => {
      b.badgeKey = t.id;
      b.color = t.badgeColor;
      b.textColor = DEFAULT_BADGE_TEXT_COLOR;
      b.shape = t.shape;
      this.assignBadgeCatalogIcon(
        b,
        t.icon ? { name: t.icon, url: this.resolveIconUrl(t.icon) } : null,
      );
      b.iconPosition = "prefix";
      // Thai keeps the preset's Thai text (its English when none); English and
      // every other active locale get the English label.
      const thLocale = EnrichmentPricing.LANG_TO_LOCALE.TH;
      const enLocale = EnrichmentPricing.LANG_TO_LOCALE.EN;
      const en = t.text.EN ?? "";
      const th = t.text.TH ?? en;
      const label: Record<string, string> = { [thLocale]: th, [enLocale]: en };
      for (const lang of this.languages()) {
        if (lang.locale !== thLocale) label[lang.locale] = en;
      }
      b.label = label;
    });
  }

  onBadgeLabelChange(value: LocaleMap): void {
    this.updateBadge((b) => {
      b.label = value as unknown as Record<string, string>;
    });
  }

  onBadgeColorChange(color: string): void {
    this.updateBadge((b) => {
      b.color = color;
    });
  }

  private assignBadgeCatalogIcon(
    badge: BadgeFormValue,
    choice: Pick<IconChoice, "name" | "url"> | null,
  ): void {
    badge.icon = choice?.name ?? "";
    badge.iconUrl = choice?.url ?? "";
  }

  /** Pick a CDN icon (or clear) from the icon picker. Stores the icon's stable
   *  filename and URL in `style`. */
  onBadgeIconPick(choice: IconChoice | null): void {
    this.updateBadge((b) => {
      this.assignBadgeCatalogIcon(b, choice);
    });
  }

  onBadgeIconPositionChange(pos: BadgeIconPosition): void {
    this.updateBadge((b) => {
      b.iconPosition = pos;
    });
  }

  onBadgeActiveToggle(active: boolean): void {
    this.updateBadge((b) => {
      b.isActive = active;
    });
  }

  /** Remove the badge entirely so the price round-trips with none. */
  clearBadge(): void {
    this.updateSelectedPrice((p) => {
      p.displays = (p.displays ?? []).filter(
        (d) => d.name !== BADGE_DISPLAY_NAME,
      );
    });
  }

  /* ── Badge (Detail) write-back ──────────────────────────────────────────
   *  Mirror of the promo-badge write-back, bound to BADGE_DETAIL_DISPLAY_NAME.
   *  The tier gradients are light, so template/color changes resolve a dark
   *  `textColor` from BADGE_COLOR_TEXT_DEFAULTS (white for colors not listed). */
  private updateBadgeDetail(patch: (b: BadgeFormValue) => void): void {
    this.updateSelectedPrice((p) => {
      const list = [...(p.displays ?? [])];
      const idx = list.findIndex((d) => d.name === BADGE_DETAIL_DISPLAY_NAME);
      const priorValue = idx >= 0 ? list[idx].value : undefined;
      const current = parseBadgeValue(priorValue) ?? emptyBadge();
      patch(current);

      if (isBadgeEmpty(current)) {
        if (idx >= 0) {
          list.splice(idx, 1);
          p.displays = list;
        }
        return;
      }

      const item: EnrichmentDisplayItem = {
        name: BADGE_DETAIL_DISPLAY_NAME,
        valueType: "object",
        value: buildBadgeValue(current, priorValue),
      };
      if (idx >= 0) list[idx] = item;
      else list.push(item);
      p.displays = list;
    });
  }

  /** Writes the complete, nested `displayPopupUpSell` contract while retaining
   * unknown keys / inactive locales supplied by the backend. */
  private updateUpsellPopup(
    patch: (popup: UpsellPopupFormValue) => void,
  ): void {
    this.updateSelectedPrice((price) => {
      const displays = [...(price.displays ?? [])];
      const index = displays.findIndex(
        (display) => display.name === UPSELL_POPUP_DISPLAY_NAME,
      );
      const priorValue = index >= 0 ? displays[index].value : undefined;
      const prior = recordOf(priorValue);
      const popup = parseUpsellPopupValue(priorValue);
      patch(popup);

      const nextValue: Record<string, unknown> = {
        ...prior,
        validFor: {
          ...recordOf(prior["validFor"]),
          startDateTime: popup.startDateTime,
          endDateTime: popup.endDateTime,
        },
        name: popup.name,
        badge: buildBadgeValue(popup.badge, prior["badge"]),
      };
      // Backend contract migrated from `content.{locale}.header/title` to
      // rich-text `name.{locale}`. Do not keep the retired shape in PATCHes.
      delete nextValue["content"];
      if (popup.relatedPriceId) {
        nextValue["relatedPrice"] = {
          ...recordOf(prior["relatedPrice"]),
          id: popup.relatedPriceId,
          "@referredType": "ProductOfferingPrice",
        };
      } else {
        delete nextValue["relatedPrice"];
      }

      const item: EnrichmentDisplayItem = {
        name: UPSELL_POPUP_DISPLAY_NAME,
        valueType: "object",
        value: nextValue,
      };
      if (index >= 0) displays[index] = item;
      else displays.push(item);
      price.displays = displays;
    });
  }

  /** Remove the pop-up entirely. The backend clears `displayPopupUpSell`
   *  only by omission, so the item leaves the array rather than going blank. */
  clearUpsellPopup(): void {
    this.updateSelectedPrice((price) => {
      price.displays = (price.displays ?? []).filter(
        (display) => display.name !== UPSELL_POPUP_DISPLAY_NAME,
      );
    });
    this.previewSpotlight.upsellPopupOpen.set(false);
  }

  protected upsellNameFor(locale: string): string {
    return this.upsellName()[locale] ?? "";
  }

  onUpsellNameChange(locale: string, value: string): void {
    this.updateUpsellPopup((popup) => {
      popup.name = { ...popup.name, [locale]: value };
    });
  }

  onUpsellBadgeLabelChange(value: LocaleMap): void {
    this.updateUpsellPopup((popup) => {
      popup.badge.label = stringMapOf(value);
    });
  }

  onUpsellBadgeColorChange(color: string): void {
    this.updateUpsellPopup((popup) => {
      popup.badge.color = color;
      popup.badge.textColor = this.badgeDetailTextFor(color);
    });
  }

  protected toggleUpsellBadgePreset(): void {
    this.upsellBadgePresetOpen.update((open) => !open);
  }

  protected closeUpsellBadgePreset(): void {
    this.upsellBadgePresetOpen.set(false);
  }

  protected selectUpsellBadgePreset(id: string): void {
    if (!id) {
      this.updateUpsellPopup((popup) => {
        popup.badge.badgeKey = "";
      });
      this.closeUpsellBadgePreset();
      return;
    }
    const template = this.upsellBadgeTemplates.find((item) => item.id === id);
    if (template) this.applyUpsellBadgeTemplate(template);
    this.closeUpsellBadgePreset();
  }

  private applyUpsellBadgeTemplate(template: EnrichmentBadgeTemplate): void {
    this.updateUpsellPopup((popup) => {
      const badge = popup.badge;
      badge.badgeKey = template.id;
      badge.color = template.badgeColor;
      badge.textColor =
        template.textColor ?? this.badgeDetailTextFor(template.badgeColor);
      badge.shape = template.shape;
      this.assignBadgeCatalogIcon(
        badge,
        template.icon
          ? { name: template.icon, url: this.resolveIconUrl(template.icon) }
          : null,
      );
      badge.iconPosition = "prefix";

      const thLocale = EnrichmentPricing.LANG_TO_LOCALE.TH;
      const enLocale = EnrichmentPricing.LANG_TO_LOCALE.EN;
      const en = template.text.EN ?? "";
      const th = template.text.TH ?? en;
      const label: Record<string, string> = { [thLocale]: th, [enLocale]: en };
      for (const language of this.languages()) {
        if (language.locale !== thLocale) label[language.locale] = en;
      }
      badge.label = label;
    });
  }

  onUpsellBadgeIconPick(choice: IconChoice | null): void {
    this.updateUpsellPopup((popup) => {
      this.assignBadgeCatalogIcon(popup.badge, choice);
    });
  }

  onUpsellStartDateChange(value: unknown): void {
    const date = this.dayWithKeptClock(
      value,
      this.upsellStartTime() || "00:00",
    );
    this.updateUpsellPopup((popup) => {
      // A date-only selection is an explicit start at 00:00; RFC3339 keeps
      // that clock on the wire even when the author leaves Time untouched.
      popup.startDateTime =
        date instanceof Date ? formatUpsellPopupDateTime(date) : "";
    });
  }

  onUpsellBadgeIconPositionChange(pos: BadgeIconPosition): void {
    this.updateUpsellPopup((popup) => {
      popup.badge.iconPosition = pos;
    });
  }

  onUpsellBadgeActiveToggle(active: boolean): void {
    this.updateUpsellPopup((popup) => {
      popup.badge.isActive = active;
    });
  }

  onUpsellEndDateChange(value: unknown): void {
    const date = this.dayWithKeptClock(value, this.upsellEndTime() || "23:59");
    this.updateUpsellPopup((popup) => {
      // A date-only selection is an explicit end at 23:59; do not omit the
      // clock simply because the author accepted the default Time value.
      popup.endDateTime =
        date instanceof Date ? formatUpsellPopupDateTime(date) : "";
    });
  }

  onUpsellStartTimeChange(value: unknown): void {
    const date = parseCivrDate(this.upsellPopup().startDateTime);
    if (!date) return;
    this.updateUpsellPopup((popup) => {
      popup.startDateTime = formatUpsellPopupDateTime(
        withClock(date, this.civrAsText(value) || "00:00"),
      );
    });
  }

  onUpsellEndTimeChange(value: unknown): void {
    const date = parseCivrDate(this.upsellPopup().endDateTime);
    if (!date) return;
    this.updateUpsellPopup((popup) => {
      popup.endDateTime = formatUpsellPopupDateTime(
        withClock(date, this.civrAsText(value) || "23:59"),
      );
    });
  }

  /** Foreground ink for a detail color: explicit override wins, else the
   *  per-color default, else white. */
  private badgeDetailTextFor(color: string): string {
    return (
      BADGE_COLOR_TEXT_DEFAULTS[color as BadgeColorType] ??
      DEFAULT_BADGE_TEXT_COLOR
    );
  }

  onBadgeDetailPresetSelect(id: string): void {
    if (!id) {
      this.clearBadgeDetail();
      return;
    }
    const t = this.badgeDetailTemplates.find((x) => x.id === id);
    if (t) this.applyBadgeDetailTemplate(t);
  }

  applyBadgeDetailTemplate(t: EnrichmentBadgeTemplate): void {
    this.updateBadgeDetail((b) => {
      b.badgeKey = t.id;
      b.color = t.badgeColor;
      b.textColor = t.textColor ?? this.badgeDetailTextFor(t.badgeColor);
      b.shape = t.shape;
      this.assignBadgeCatalogIcon(
        b,
        t.icon ? { name: t.icon, url: this.resolveIconUrl(t.icon) } : null,
      );
      b.iconPosition = "prefix";
      const thLocale = EnrichmentPricing.LANG_TO_LOCALE.TH;
      const enLocale = EnrichmentPricing.LANG_TO_LOCALE.EN;
      const en = t.text.EN ?? "";
      const th = t.text.TH ?? en;
      const label: Record<string, string> = { [thLocale]: th, [enLocale]: en };
      for (const lang of this.languages()) {
        if (lang.locale !== thLocale) label[lang.locale] = en;
      }
      b.label = label;
    });
  }

  onBadgeDetailLabelChange(value: LocaleMap): void {
    this.updateBadgeDetail((b) => {
      b.label = value as unknown as Record<string, string>;
    });
  }

  onBadgeDetailColorChange(color: string): void {
    this.updateBadgeDetail((b) => {
      b.color = color;
      // Picking a tier swatch sets its readable ink; a later manual textColor
      // control (if added) would override this.
      b.textColor = this.badgeDetailTextFor(color);
    });
  }

  onBadgeDetailIconPick(choice: IconChoice | null): void {
    this.updateBadgeDetail((b) => {
      this.assignBadgeCatalogIcon(b, choice);
    });
  }

  onBadgeDetailIconPositionChange(pos: BadgeIconPosition): void {
    this.updateBadgeDetail((b) => {
      b.iconPosition = pos;
    });
  }

  onBadgeDetailActiveToggle(active: boolean): void {
    this.updateBadgeDetail((b) => {
      b.isActive = active;
    });
  }

  /** Remove the detail badge entirely so the price round-trips with none. */
  clearBadgeDetail(): void {
    this.updateSelectedPrice((p) => {
      p.displays = (p.displays ?? []).filter(
        (d) => d.name !== BADGE_DETAIL_DISPLAY_NAME,
      );
    });
  }

  /* ── Service Enrichment write-back ─────────────────────────────────── */

  private updateDisplay(
    index: number,
    patch: (d: EnrichmentDisplayItem) => void,
  ): void {
    this.updateSelectedPrice((p) => {
      const list = p.displays ?? [];
      if (index < 0 || index >= list.length) return;
      const current = list[index];
      const next: EnrichmentDisplayItem = {
        ...current,
        value:
          current.value && typeof current.value === "object"
            ? { ...(current.value as object) }
            : current.value,
      };
      patch(next);
      p.displays = [...list.slice(0, index), next, ...list.slice(index + 1)];
    });
  }

  private patchDisplayValue(
    index: number,
    key:
      | "name"
      | "description"
      | "supportingText"
      | "downloadLabel"
      | "uploadLabel",
    value: LocaleMap,
  ): void {
    this.updateDisplay(index, (d) => {
      const v = (d.value ?? {}) as Record<string, unknown>;
      d.value = { ...v, [key]: value };
    });
  }

  onInnerNameChange(index: number, value: LocaleMap): void {
    this.patchDisplayValue(index, "name", value);
  }
  onInnerDescriptionChange(index: number, value: LocaleMap): void {
    this.patchDisplayValue(index, "description", value);
  }
  onInnerSupportingTextChange(index: number, value: LocaleMap): void {
    this.patchDisplayValue(index, "supportingText", value);
  }
  /* Speed-panel Download / Upload wording write-back (broadband displays).
   * Same rails as name/description — `patchDisplayValue` overlays one key and
   * preserves the rest of `value` (kind, characteristics, …). */
  onDownloadLabelChange(index: number, value: LocaleMap): void {
    this.patchDisplayValue(index, "downloadLabel", value);
  }
  onUploadLabelChange(index: number, value: LocaleMap): void {
    this.patchDisplayValue(index, "uploadLabel", value);
  }
  /* ── Per-service Notes (repeatable array) ──────────────────────────────
   *  Notes live at `display.value.notes` (LocaleMap[]). All edits route
   *  through `updateDisplay()` so the commit/preview wiring is identical to
   *  the other display fields. `updateNotes` mirrors `innerNotes`' legacy
   *  seeding so the array the user sees matches the array we mutate. */
  private updateNotes(
    index: number,
    mutate: (notes: LocaleMap[]) => void,
  ): void {
    this.updateDisplay(index, (d) => {
      const v = (d.value ?? {}) as Record<string, unknown>;
      // Always a fresh array (innerNotes / .map both copy), so in-place
      // mutation here never touches the stored model.
      const notes = Array.isArray(v["notes"])
        ? (v["notes"] as unknown[]).map((n) => this.asLocaleMap(n))
        : this.innerNotes(d); // seed from legacy note on first touch
      mutate(notes);
      d.value = { ...v, notes };
    });
  }

  onNoteChange(
    displayIndex: number,
    noteIndex: number,
    value: LocaleMap,
  ): void {
    this.updateNotes(displayIndex, (notes) => {
      notes[noteIndex] = value;
    });
  }

  addNote(displayIndex: number): void {
    this.updateNotes(displayIndex, (notes) => {
      notes.push({});
    });
  }

  removeNote(displayIndex: number, noteIndex: number): void {
    this.updateNotes(displayIndex, (notes) => {
      notes.splice(noteIndex, 1);
    });
  }

  moveNoteUp(displayIndex: number, noteIndex: number): void {
    if (noteIndex <= 0) return;
    this.updateNotes(displayIndex, (notes) => {
      [notes[noteIndex - 1], notes[noteIndex]] = [
        notes[noteIndex],
        notes[noteIndex - 1],
      ];
    });
  }

  moveNoteDown(displayIndex: number, noteIndex: number): void {
    this.updateNotes(displayIndex, (notes) => {
      if (noteIndex >= notes.length - 1) return;
      [notes[noteIndex + 1], notes[noteIndex]] = [
        notes[noteIndex],
        notes[noteIndex + 1],
      ];
    });
  }

  onInnerAmountChange(index: number, raw: string): void {
    const n = raw.trim() === "" ? null : Number(raw);
    this.updateDisplay(index, (d) => {
      const v = (d.value ?? {}) as Record<string, unknown>;
      const u = (v["unitOfMeasure"] ?? {}) as Record<string, unknown>;
      d.value = {
        ...v,
        unitOfMeasure: {
          ...u,
          amount: Number.isFinite(n as number) ? (n as number) : null,
        },
      };
    });
  }

  onInnerUnitChange(index: number, value: LocaleMap): void {
    this.updateDisplay(index, (d) => {
      const v = (d.value ?? {}) as Record<string, unknown>;
      const u = (v["unitOfMeasure"] ?? {}) as Record<string, unknown>;
      d.value = { ...v, unitOfMeasure: { ...u, units: value } };
    });
  }

  /* ── Broadband Speed combined-card write-back ──────────────────────────
   *  Shared fields (Unit / Description / Notes) write to BOTH the download
   *  and upload displays in one commit so they never drift. Per-direction
   *  fields (Name, read-only Amount, Icon) keep using the existing
   *  per-display handlers (`onInnerNameChange`,
   *  `onDisplayAttachmentsChange`) against each `__index`. */

  /** Overlay a `value` patch onto both broadbandspeed displays at once. */
  private updateBroadbandShared(
    patch: (value: Record<string, unknown>) => void,
  ): void {
    const pair = this.broadbandSpeedPair();
    if (!pair) return;
    const indices = [pair.downloadIndex, pair.uploadIndex].filter(
      (i): i is number => i != null,
    );
    if (indices.length === 0) return;
    this.updateSelectedPrice((p) => {
      const list = [...(p.displays ?? [])];
      for (const idx of indices) {
        const current = list[idx];
        if (!current) continue;
        const value =
          current.value && typeof current.value === "object"
            ? { ...(current.value as Record<string, unknown>) }
            : {};
        patch(value);
        list[idx] = { ...current, value };
      }
      p.displays = list;
    });
  }

  onBroadbandUnitChange(value: LocaleMap): void {
    this.updateBroadbandShared((v) => {
      const u = (v["unitOfMeasure"] ?? {}) as Record<string, unknown>;
      v["unitOfMeasure"] = { ...u, units: value };
    });
  }

  onBroadbandDescriptionChange(value: LocaleMap): void {
    this.updateBroadbandShared((v) => {
      v["description"] = value;
    });
  }

  /* Icon is deliberately NOT shared: each direction owns its own
   * `value.attachment`, edited in its own inner card through the generic
   * `onDisplayAttachmentsChange(__index, …)` rail, so Marketing can bind a
   * distinct download / upload icon even though the two displays share one
   * combined card. The preview falls back to the static direction icon when a
   * direction carries none. */

  /** Shared Notes editing for the broadband pair — mirrors `updateNotes` but
   *  writes the same notes array to both displays. Seeds from the download
   *  display (or upload when download is absent) via `broadbandSeed`. Each
   *  display gets its own cloned array so they never share references. */
  private updateBroadbandNotes(mutate: (notes: LocaleMap[]) => void): void {
    const seed = this.broadbandSeed();
    if (!seed) return;
    const notes = this.innerNotes(seed); // fresh copy (innerNotes maps/copies)
    mutate(notes);
    this.updateBroadbandShared((v) => {
      v["notes"] = notes.map((n) => ({ ...n }));
    });
  }

  onBroadbandNoteChange(noteIndex: number, value: LocaleMap): void {
    this.updateBroadbandNotes((notes) => {
      notes[noteIndex] = value;
    });
  }
  addBroadbandNote(): void {
    this.updateBroadbandNotes((notes) => {
      notes.push({});
    });
  }
  removeBroadbandNote(noteIndex: number): void {
    this.updateBroadbandNotes((notes) => {
      notes.splice(noteIndex, 1);
    });
  }
  moveBroadbandNoteUp(noteIndex: number): void {
    if (noteIndex <= 0) return;
    this.updateBroadbandNotes((notes) => {
      [notes[noteIndex - 1], notes[noteIndex]] = [
        notes[noteIndex],
        notes[noteIndex - 1],
      ];
    });
  }
  moveBroadbandNoteDown(noteIndex: number): void {
    this.updateBroadbandNotes((notes) => {
      if (noteIndex >= notes.length - 1) return;
      [notes[noteIndex + 1], notes[noteIndex]] = [
        notes[noteIndex],
        notes[noteIndex + 1],
      ];
    });
  }

  onSortableDrop(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex === event.currentIndex) return;
    const sortable = this.sortableDisplays();
    const from = sortable[event.previousIndex]?.__index;
    const to = sortable[event.currentIndex]?.__index;
    if (from == null || to == null) return;

    this.updateSelectedPrice((p) => {
      const list = [...(p.displays ?? [])];
      moveItemInArray(list, from, to);
      p.displays = list;
    });
  }

  /** Move a sortable display one position up/down (within the sortable
   *  subset — pinned displays are skipped). Click-based alternative to
   *  drag-and-drop for users who can't drag (keyboard, mobile). */
  moveDisplayUp(absoluteIndex: number): void {
    const sortable = this.sortableDisplays();
    const idx = sortable.findIndex((d) => d.__index === absoluteIndex);
    if (idx <= 0) return;
    const prev = sortable[idx - 1].__index;
    this.swapDisplays(absoluteIndex, prev);
  }

  moveDisplayDown(absoluteIndex: number): void {
    const sortable = this.sortableDisplays();
    const idx = sortable.findIndex((d) => d.__index === absoluteIndex);
    if (idx < 0 || idx >= sortable.length - 1) return;
    const next = sortable[idx + 1].__index;
    this.swapDisplays(absoluteIndex, next);
  }

  private swapDisplays(a: number, b: number): void {
    this.updateSelectedPrice((p) => {
      const list = [...(p.displays ?? [])];
      if (a < 0 || b < 0 || a >= list.length || b >= list.length) return;
      [list[a], list[b]] = [list[b], list[a]];
      p.displays = list;
    });
  }

  /** True when this sortable display is the first/last in its group —
   *  disables the corresponding arrow button so the user can't
   *  no-op-click off the end. */
  isFirstSortable(absoluteIndex: number): boolean {
    return this.sortableDisplays()[0]?.__index === absoluteIndex;
  }

  isLastSortable(absoluteIndex: number): boolean {
    const list = this.sortableDisplays();
    return list[list.length - 1]?.__index === absoluteIndex;
  }

  /** Run-numbered name for a new custom display ("displayOther"). The
   *  catalog uses the display `name` as a stable identifier, so two
   *  bare "displayOther" rows would collide. We scan for existing
   *  `displayOther` / `displayOther{N}` names and pick the next free
   *  integer suffix (1-based). */
  private nextDisplayOtherName(): string {
    const re = /^displayOther(\d*)$/;
    let maxN = 0;
    for (const d of this.displays()) {
      const m = re.exec(d?.name ?? "");
      if (!m) continue;
      const n = m[1] ? Number.parseInt(m[1], 10) : 0;
      if (n > maxN) maxN = n;
    }
    return `displayOther${maxN + 1}`;
  }

  addDisplayOther(): void {
    const name = this.nextDisplayOtherName();
    // Seed the required Name with empty per-locale slots (not a bare `{}`): an
    // empty object has no locale keys, so the coverage walk + readiness gate
    // can't see it — the new service would contribute nothing to completion %.
    // Per-locale empties make it count immediately as a gap to fill. (#44)
    const emptyName: LocaleMap = Object.fromEntries(
      this.languages().map((l) => [l.locale, ""]),
    );
    this.updateSelectedPrice((p) => {
      const list = p.displays ?? [];
      p.displays = [
        ...list,
        {
          name,
          description: null,
          valueType: "object",
          value: {
            // New services start as kind "other" — a definite state the
            // kind select can re-map (BA list), never an absent key.
            kind: "other",
            name: emptyName,
            description: {},
            note: {},
            notes: [],
            unitOfMeasure: { amount: null, units: {} },
            attachment: [],
          },
        },
      ];
    });
  }

  /** Switch a service between the Display / Non Display sections. Writes the
   *  boolean explicitly both ways (never deletes the key) so BE always sees a
   *  definite state; the item itself never leaves `displays[]`, which is what
   *  preserves its position when it is restored. */
  setDisplayRemoved(index: number, removed: boolean): void {
    this.updateDisplay(index, (d) => {
      d.isRemoved = removed;
    });
  }

  /** True for user-created services (`displayOther`, `displayOther2`, …) —
   *  the only kind that may be deleted. Catalog-seeded displays are only
   *  ever soft-removed via `isRemoved` so BE keeps the resource. */
  isOtherDisplay(d: Pick<EnrichmentDisplayItem, "name">): boolean {
    return /^displayOther\d*$/.test(d?.name ?? "");
  }

  /** Delete a user-created displayOther service. SOFT delete per BE
   *  contract: stamp `isDeleted: true` and keep the item in `displays[]` so
   *  the flag travels on the wire and BE drops the resource — never splice
   *  it out locally (a hard-removed item would simply be missing from the
   *  PATCH, which BE cannot distinguish from "unchanged"). Both editor
   *  sections and every consumer (preview, readiness) filter it out. */
  removeDisplay(index: number): void {
    this.updateSelectedPrice((p) => {
      const list = p.displays ?? [];
      if (index < 0 || index >= list.length) return;
      // Delete is reserved for user-created displayOther items; catalog
      // displays can only move to Non Display (setDisplayRemoved).
      if (!this.isOtherDisplay(list[index])) return;
      const next = { ...list[index], isDeleted: true };
      p.displays = [...list.slice(0, index), next, ...list.slice(index + 1)];
    });
  }

  /** BA-fixed kind list for user-created displayOther services. Values are
   *  the wire values; labels stay identical so what the enricher picks is
   *  exactly what BE stores. */
  readonly displayKindOptions: SelectOption[] = DISPLAY_KIND_VALUES.map(
    (k) => ({ label: k, value: k }),
  );

  /** Current `value.kind` of a display, for the displayOther kind select. */
  displayKind(d: EnrichmentDisplayItem): string | null {
    const kind = (d?.value as { kind?: unknown } | undefined)?.kind;
    return typeof kind === "string" && kind.trim() ? kind : null;
  }

  /** Kind pill on a service row. The display's own uploaded icon wins over
   *  the kind default, as in the customer preview. */
  serviceKind(d: EnrichmentDisplayItem): ServiceKindBadge | null {
    const attachment = (d?.value as { attachment?: unknown } | undefined)
      ?.attachment;
    return serviceKindBadge(
      this.displayKind(d),
      firstIconUrl(attachment, this.languageMode.activeLocale()),
    );
  }

  /** Icon urls that failed to load; their pills show the kind abbreviation. */
  readonly brokenKindIcons = signal<ReadonlySet<string>>(new Set());

  markKindIconBroken(url: string): void {
    this.brokenKindIcons.update((s) => new Set(s).add(url));
  }

  /** Write `value.kind` on a displayOther service. Same overlay rails as
   *  `patchDisplayValue` — every sibling key of `value` survives. Changing
   *  kind re-gates dependent UI live (e.g. `internet` unlocks the
   *  supportingText field; the preview icon/benefit mapping follows). */
  setDisplayKind(index: number, kind: string | null): void {
    this.updateDisplay(index, (d) => {
      const v = (d.value ?? {}) as Record<string, unknown>;
      d.value = { ...v, kind: kind ?? "" };
    });
  }
}

/** Direction of a broadbandspeed display, from the stable display KEY
 *  (`displayBroadbandServiceDownload` / `...Upload`); `value.name` is only a
 *  fallback because it is editable wording. Empty when neither says which —
 *  matches the preview's `broadbandSpeedDirection` so form and preview agree. */
export function broadbandSpeedDirection(
  d: EnrichmentDisplayItem,
): "download" | "upload" | "" {
  const key = (d?.name ?? "").toLowerCase();
  if (key.includes("download")) return "download";
  if (key.includes("upload")) return "upload";
  const name = resolveLocalized(
    (d?.value as { name?: unknown })?.name,
  ).toLowerCase();
  if (name.includes("download")) return "download";
  if (name.includes("upload")) return "upload";
  return "";
}

/** Explicit, Marketing-facing labels for the catalog's `externalIdentifierType`
 *  tokens. The map order also fixes the chip display order (most useful first)
 *  via `externalIdRank`; anything unmapped falls back to a humanized token and
 *  sorts last. */
const EXTERNAL_ID_LABELS: readonly (readonly [string, string])[] = [
  ["ProductOfferingPriceCode", "Price code"],
  ["ProductOfferingPriceId", "Price ID"],
  ["ProductOfferingId", "Offering ID"],
  ["ProductOfferingVariantId", "Variant ID"],
] as const;

/** Label for an `externalIdentifierType` — explicit map first, then a humanized
 *  fallback so unknown future types still read cleanly. Empty token → "Code". */
export function externalIdLabel(type: string | undefined | null): string {
  const key = (type ?? "").trim();
  const hit = EXTERNAL_ID_LABELS.find(([t]) => t === key);
  if (hit) return hit[1];
  return humanizePriceType(key) || "Code";
}

/** Display-order rank for an `externalIdentifierType` — its position in
 *  `EXTERNAL_ID_LABELS`; unmapped types sort after the known ones. */
function externalIdRank(type: string): number {
  const idx = EXTERNAL_ID_LABELS.findIndex(([t]) => t === type);
  return idx >= 0 ? idx : EXTERNAL_ID_LABELS.length;
}

/** Humanize a TMF `priceType` token for display: splits camelCase and
 *  snake/kebab tokens, then Title-Cases each word ("oneTime" → "One Time",
 *  "onetime" → "One Time", "recurring" → "Recurring"). Empty / missing → "". */
export function humanizePriceType(value: string | undefined | null): string {
  if (!value || !value.trim()) return "";
  const trimmed = value.trim();
  if (trimmed.toLowerCase().replaceAll(/[_-]+/g, "") === "onetime") {
    return "One Time";
  }
  return trimmed
    .trim()
    .replaceAll(/[_-]+/g, " ")
    .replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}
